import "../../../styles/planner.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import Layout from "../../Layout.tsx";
import Modal from "../../Modal.tsx";
import VoyagePicker from "../../FormFields/RenderVoyagePicker.tsx";
import { useInitialPlannerData } from "../../hooks/useInitialPlannerData.tsx";
import { useRefreshVoyageData } from "../../hooks/useRefreshVoyageData.tsx";

import { showModal } from "../../../data/modal";
import { addSelectedTerminalCallFilterValue } from "../../../data/filter.tsx";
import { selectedFilteredVoyage } from "../../../selectors/filterSelector.js";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  StorefrontOutlined as TerminalIcon,
  ViewModule as VesselIcon,
  South as LoadingIcon,
  North as UnloadingIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as FileIcon,
  Save as SaveIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";

import { alpha } from "@mui/material/styles";
import PlannerViewSwitch from "../../PlannerViewSwitch.tsx";

const WRAPPER_BORDER_RADIUS = "12px";
const DEFAULT_TERMINAL_COLOR = "#dbeafe";

const LOADING_COLOR = "#ca8a04";
const LOADING_BACKGROUND = "rgba(234, 179, 8, 0.13)";
const LOADING_BORDER = "rgba(234, 179, 8, 0.24)";

const UNLOADING_COLOR = "#16a34a";
const UNLOADING_BACKGROUND = "rgba(22, 163, 74, 0.08)";
const UNLOADING_BORDER = "rgba(22, 163, 74, 0.18)";

const SEARCH_COLOR = "#1d4ed8";
const SEARCH_BACKGROUND = "rgba(37, 99, 235, 0.08)";
const SEARCH_BORDER = "rgba(37, 99, 235, 0.2)";

type TerminalCallMode = "loading" | "unloading";

const normalizeCollection = (value: any) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const normalizeApiId = (value: any) => {
  if (!value) return null;
  if (typeof value === "string") return value;

  return value["@id"] ?? null;
};

const normalizeNumericId = (value: any) => {
  const apiId = normalizeApiId(value);

  if (apiId) {
    return apiId.split("/").filter(Boolean).pop();
  }

  if (value?.id !== null && value?.id !== undefined) {
    return String(value.id);
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value.split("/").filter(Boolean).pop();
  }

  return "";
};

const getComparableIds = (value: any) => {
  if (!value) {
    return [];
  }

  const ids = [
    normalizeApiId(value),
    normalizeNumericId(value),
    value?.id !== null && value?.id !== undefined ? String(value.id) : null,
  ].filter(Boolean) as string[];

  return Array.from(new Set(ids));
};

const getEntityId = (value: any) => {
  return (
    normalizeApiId(value) ||
    (value?.id !== null && value?.id !== undefined ? String(value.id) : "") ||
    normalizeNumericId(value) ||
    ""
  );
};

const getTerminalNameFromTerminal = (terminal?: any, t?: any) => {
  return (
    terminal?.name ||
    terminal?.abbreviation ||
    terminal?.city ||
    (t ? t("unknownTerminal") : "Unknown terminal")
  );
};

const getTerminalName = (terminalCall?: any, t?: any) => {
  return getTerminalNameFromTerminal(terminalCall?.terminal, t);
};

const getTerminalCallNumber = (terminalCall?: any) => {
  return (
    terminalCall?.reference ||
    terminalCall?.code ||
    terminalCall?.name ||
    terminalCall?.terminal?.abbreviation ||
    normalizeNumericId(terminalCall) ||
    "—"
  );
};

const getTerminalCallColor = (terminalCall?: any) => {
  return terminalCall?.terminal?.color || DEFAULT_TERMINAL_COLOR;
};

const getTerminalCallDateValue = (terminalCall?: any) => {
  return (
    terminalCall?.planned ||
    terminalCall?.arrival ||
    terminalCall?.operationStart ||
    terminalCall?.operationEnd ||
    terminalCall?.departure ||
    null
  );
};

const getTerminalCallDate = (terminalCall?: any) => {
  const value = getTerminalCallDateValue(terminalCall);

  if (!value) {
    return null;
  }

  const date = dayjs(value);

  return date.isValid() ? date : null;
};

const getTerminalCallDateTimeKey = (terminalCall?: any) => {
  const date = getTerminalCallDate(terminalCall);

  if (!date?.isValid()) {
    return "";
  }

  return String(date.valueOf());
};

const getTerminalCallSameDateTimeLabel = (terminalCall?: any, t?: any) => {
  const date = getTerminalCallDate(terminalCall);

  if (!date?.isValid()) {
    return t ? t("noPlannedDate") : "No planned date";
  }

  return date.format("DD MMM YYYY HH:mm");
};

const formatTerminalCallTime = (terminalCall?: any) => {
  const date = getTerminalCallDate(terminalCall);

  if (!date) {
    return "—";
  }

  return date.format("HH:mm");
};

const formatTerminalCallFullDate = (terminalCall?: any, t?: any) => {
  const date = getTerminalCallDate(terminalCall);

  if (!date?.isValid()) {
    return t ? t("noPlannedDate") : "No planned date";
  }

  return date.format("DD MMM YYYY HH:mm");
};

const formatContainerDateTime = (value?: string | null, t?: any) => {
  if (!value) {
    return t ? t("noDate") : "No date";
  }

  const date = dayjs(value);

  if (!date.isValid()) {
    return t ? t("noDate") : "No date";
  }

  return date.format("DD MMM YYYY HH:mm");
};

const formatWeightKg = (weight?: number | null) => {
  const parsedWeight = Number(weight ?? 0);

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(parsedWeight) ? parsedWeight : 0);
};

const getContainerTypeLabel = (container: any) => {
  return (
    container?.containerType?.abbreviation ||
    container?.containerType?.name ||
    container?.iso ||
    "—"
  );
};

const getContainerTypeColor = (container: any) => {
  const label = String(getContainerTypeLabel(container)).toLowerCase();

  if (label.includes("20")) {
    return {
      background: "#dcfce7",
      color: "#15803d",
    };
  }

  if (label.includes("40")) {
    return {
      background: "#dbeafe",
      color: "#1d4ed8",
    };
  }

  return {
    background: "#eef2ff",
    color: "#4338ca",
  };
};

const isTerminalCallCompleted = (terminalCall: any) => {
  const status = String(terminalCall?.status || "").toLowerCase();

  return Boolean(
    terminalCall?.departure ||
      terminalCall?.operationEnd ||
      status === "completed" ||
      status === "voltooid"
  );
};

const isTerminalCallPlanned = (terminalCall: any) => {
  const status = String(terminalCall?.status || "").toLowerCase();

  return status === "planned" || status === "gepland";
};

const getTerminalCallStatusLabel = (terminalCall: any, t: any) => {
  if (isTerminalCallCompleted(terminalCall)) {
    return t("completed");
  }

  if (isTerminalCallPlanned(terminalCall)) {
    return t("planned");
  }

  return terminalCall?.status ? t(terminalCall.status, terminalCall.status) : t("planned");
};

const getTextColorForBackground = (backgroundColor?: string | null) => {
  if (!backgroundColor || !backgroundColor.startsWith("#")) {
    return "#0f172a";
  }

  const hex = backgroundColor.replace("#", "");

  if (hex.length !== 6) {
    return "#0f172a";
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.62 ? "#0f172a" : "#ffffff";
};

const sortTerminalCallsByDate = (terminalCalls: any[]) => {
  return [...terminalCalls].sort((a, b) => {
    const aDate = getTerminalCallDate(a);
    const bDate = getTerminalCallDate(b);

    const aValue = aDate?.isValid()
      ? aDate.valueOf()
      : Number.MAX_SAFE_INTEGER;

    const bValue = bDate?.isValid()
      ? bDate.valueOf()
      : Number.MAX_SAFE_INTEGER;

    if (aValue !== bValue) {
      return aValue - bValue;
    }

    return getTerminalName(a).localeCompare(getTerminalName(b));
  });
};

const getTerminalCallOptionLabel = (terminalCall: any, t?: any) => {
  const date = getTerminalCallDate(terminalCall);
  const dateLabel = date?.isValid()
    ? date.format("DD MMM YYYY HH:mm")
    : t
    ? t("noDate")
    : "No date";

  return `${dateLabel} - ${getTerminalName(terminalCall, t)}`;
};

const sortContainersByUid = (containers: any[]) => {
  return [...containers].sort((a, b) => {
    const aUid = String(a?.uid || normalizeNumericId(a) || "");
    const bUid = String(b?.uid || normalizeNumericId(b) || "");

    return aUid.localeCompare(bUid);
  });
};

const getContainerWeight = (container: any) => {
  const weight = Number(container?.weight ?? 0);

  return Number.isFinite(weight) ? weight : 0;
};

const getTrajectoryKeyFromContainer = (container: any) => {
  const loadingTerminalCallId =
    normalizeApiId(container?.loadingTerminalCall) ||
    normalizeApiId(container?.loadingTerminal) ||
    "null";

  const unloadingTerminalCallId =
    normalizeApiId(container?.unloadingTerminalCall) ||
    normalizeApiId(container?.unloadingTerminal) ||
    "null";

  return `${loadingTerminalCallId}->${unloadingTerminalCallId}`;
};

const getContainerTerminalLabel = (
  container: any,
  type: "loading" | "unloading"
) => {
  const terminalCall =
    type === "loading"
      ? container?.loadingTerminalCall
      : container?.unloadingTerminalCall;

  const terminal =
    type === "loading" ? container?.loadingTerminal : container?.unloadingTerminal;

  return (
    getTerminalCallNumber(terminalCall) ||
    terminal?.abbreviation ||
    terminal?.name ||
    "—"
  );
};

const getTrajectoryTerminalCall = (
  container: any,
  type: "loading" | "unloading"
) => {
  return type === "loading"
    ? container?.loadingTerminalCall
    : container?.unloadingTerminalCall;
};

const getTrajectorySortValue = (container: any, mode: TerminalCallMode) => {
  const terminalCall = getTrajectoryTerminalCall(container, mode);
  const date = getTerminalCallDate(terminalCall);

  return date?.isValid() ? date.valueOf() : Number.MAX_SAFE_INTEGER;
};

const buildTrajectoriesFromContainers = (
  containers: any[],
  mode: TerminalCallMode
) => {
  const trajectoryMap = new Map<string, any>();

  containers.forEach((container: any) => {
    const key = getTrajectoryKeyFromContainer(container);

    if (!trajectoryMap.has(key)) {
      trajectoryMap.set(key, {
        key,
        mode,
        containers: [],
        totalWeight: 0,
      });
    }

    const trajectory = trajectoryMap.get(key);

    trajectory.containers.push(container);
    trajectory.totalWeight += getContainerWeight(container);
  });

  return Array.from(trajectoryMap.values())
    .map((trajectory: any) => {
      const firstContainer = trajectory.containers[0];

      const loadingTerminalCall = getTrajectoryTerminalCall(firstContainer, "loading");
      const unloadingTerminalCall = getTrajectoryTerminalCall(
        firstContainer,
        "unloading"
      );

      const loadingLabel = getContainerTerminalLabel(firstContainer, "loading");
      const unloadingLabel = getContainerTerminalLabel(firstContainer, "unloading");

      return {
        ...trajectory,
        loadingTerminalCall,
        unloadingTerminalCall,
        loadingLabel,
        unloadingLabel,
        sortValue: getTrajectorySortValue(firstContainer, mode),
        title:
          mode === "unloading"
            ? `${loadingLabel} ← ${unloadingLabel}`
            : `${loadingLabel} → ${unloadingLabel}`,
        containers: sortContainersByUid(trajectory.containers),
      };
    })
    .sort((a: any, b: any) => {
      if (a.sortValue !== b.sortValue) {
        return a.sortValue - b.sortValue;
      }

      return String(a.title).localeCompare(String(b.title));
    });
};

const getContainerSearchText = (container: any) => {
  return [
    container?.uid,
    container?.reference,
    container?.name,
    container?.containerType?.abbreviation,
    container?.containerType?.name,
    container?.iso,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const containerMatchesSearchTerm = (container: any, searchTerm: string) => {
  const term = searchTerm.trim().toLowerCase();

  if (!term) {
    return true;
  }

  return String(container?.searchText || getContainerSearchText(container)).includes(
    term
  );
};

const getRandomDateBetweenTerminalCalls = (
  previousTerminalCall?: any,
  nextTerminalCall?: any
) => {
  const previousDate = getTerminalCallDate(previousTerminalCall);
  const nextDate = getTerminalCallDate(nextTerminalCall);

  if (previousDate?.isValid() && nextDate?.isValid()) {
    const previousValue = previousDate.valueOf();
    const nextValue = nextDate.valueOf();

    if (nextValue > previousValue) {
      const randomValue =
        previousValue + Math.floor(Math.random() * (nextValue - previousValue));

      return dayjs(randomValue).toISOString();
    }
  }

  if (previousDate?.isValid()) {
    const randomMinutes = 30 + Math.floor(Math.random() * 330);

    return previousDate.add(randomMinutes, "minute").toISOString();
  }

  if (nextDate?.isValid()) {
    const randomMinutes = 30 + Math.floor(Math.random() * 330);

    return nextDate.subtract(randomMinutes, "minute").toISOString();
  }

  const randomMinutes = 30 + Math.floor(Math.random() * 330);

  return dayjs().add(randomMinutes, "minute").toISOString();
};

const getTerminalCallPatchUrl = (terminalCall: any) => {
  const apiId = normalizeApiId(terminalCall);

  if (apiId) {
    return apiId;
  }

  const numericId = normalizeNumericId(terminalCall);

  return numericId ? `/api/terminal_calls/${numericId}` : "";
};

const escapeRegExp = (value: string) => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const HighlightedText = ({
  value,
  searchTerm,
}: {
  value: any;
  searchTerm?: string;
}) => {
  const text = String(value ?? "");
  const term = String(searchTerm || "").trim();

  if (!term) {
    return <>{text}</>;
  }

  const regex = new RegExp(`(${escapeRegExp(term)})`, "ig");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const matched = part.toLowerCase() === term.toLowerCase();

        return matched ? (
          <Box
            key={`${part}-${index}`}
            component="mark"
            sx={{
              px: 0.2,
              py: 0,
              borderRadius: "3px",
              backgroundColor: "#fef08a",
              color: "inherit",
              fontWeight: "inherit",
            }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        );
      })}
    </>
  );
};

const getContainerDisplayLabel = (container: any, t?: any) => {
  return (
    container?.uid ||
    container?.reference ||
    normalizeNumericId(container) ||
    (t ? t("noContainerId") : "No container ID")
  );
};

const isContainerPositionedOnVessel = (container: any) => {
  return Boolean(
    container?.vessel &&
      Array.isArray(container?.position) &&
      container.position.length >= 4
  );
};

const getPositionedVesselLabel = (container: any, t?: any) => {
  return (
    container?.vessel?.name ||
    container?.vessel?.abbreviation ||
    container?.position?.[3] ||
    (t ? t("vessel") : "Vessel")
  );
};

const getContainerPositionLabel = (container: any) => {
  if (!Array.isArray(container?.position)) {
    return "";
  }

  const [layer, xAxis, yAxis] = container.position;

  if (!layer || !xAxis || !yAxis) {
    return "";
  }

  return `L:${layer} X:${xAxis} Y:${yAxis}`;
};

const getContainerPositionTooltip = (container: any, t?: any) => {
  const vesselLabel = getPositionedVesselLabel(container, t);
  const positionLabel = getContainerPositionLabel(container);

  return [
    `${
      t
        ? t("positionedOnVessel", "Positioned on vessel")
        : "Positioned on vessel"
    }: ${vesselLabel}`,
    positionLabel
      ? `${t ? t("position", "Position") : "Position"}: ${positionLabel}`
      : "",
  ]
    .filter(Boolean)
    .join(" • ");
};

const PositionedOnVesselBadge = ({
  container,
  t,
}: {
  container: any;
  t: any;
}) => {
  if (!isContainerPositionedOnVessel(container)) {
    return null;
  }

  return (
    <Tooltip title={getContainerPositionTooltip(container, t)} arrow>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.45,
          px: 0.75,
          py: 0.25,
          borderRadius: "7px",
          backgroundColor: "rgba(14, 165, 233, 0.08)",
          color: "#075985",
          border: "1px solid rgba(14, 165, 233, 0.16)",
          fontSize: "0.68rem",
          fontWeight: 950,
          lineHeight: 1,
          whiteSpace: "nowrap",
          textTransform: "uppercase",
        }}
      >
        <VesselIcon sx={{ fontSize: 14 }} />
        {t("stacked", "Stacked")}
      </Box>
    </Tooltip>
  );
};

const ContainerPositionChip = ({
  container,
  t,
}: {
  container: any;
  t: any;
}) => {
  const positionLabel = getContainerPositionLabel(container);

  if (!positionLabel) {
    return null;
  }

  return (
    <Tooltip title={getContainerPositionTooltip(container, t)} arrow>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 0.65,
          py: 0.22,
          borderRadius: "7px",
          backgroundColor: "rgba(15, 23, 42, 0.045)",
          color: "#475569",
          border: "1px solid rgba(15, 23, 42, 0.1)",
          fontSize: "0.66rem",
          fontWeight: 900,
          lineHeight: 1,
          whiteSpace: "nowrap",
          textTransform: "uppercase",
        }}
      >
        {positionLabel}
      </Box>
    </Tooltip>
  );
};

const ContainerTypeBadge = ({
  container,
  searchTerm = "",
}: {
  container: any;
  searchTerm?: string;
}) => {
  const colors = getContainerTypeColor(container);

  return (
    <Box
      component="span"
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: "7px",
        backgroundColor: colors.background,
        color: colors.color,
        fontSize: "0.72rem",
        fontWeight: 900,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      <HighlightedText
        value={getContainerTypeLabel(container)}
        searchTerm={searchTerm}
      />
    </Box>
  );
};

const TerminalCallStatusBadge = ({ terminalCall, t }: any) => {
  const completed = isTerminalCallCompleted(terminalCall);
  const planned = isTerminalCallPlanned(terminalCall);

  const color = completed
    ? UNLOADING_COLOR
    : planned
    ? "#1976d2"
    : terminalCall?.labels?.[0]?.color || getTerminalCallColor(terminalCall);

  const label = getTerminalCallStatusLabel(terminalCall, t);

  return (
    <Box
      sx={{
        px: 0.65,
        py: 0.3,
        borderRadius: "8px",
        backgroundColor: color,
        color: getTextColorForBackground(color),
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.16)",
        maxWidth: 76,
        flexShrink: 0,
      }}
    >
      <Typography
        noWrap
        sx={{
          fontSize: "0.56rem",
          fontWeight: 950,
          lineHeight: 1,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TerminalCallLoadCounts = ({
  loading,
  unloading,
}: {
  loading: number;
  unloading: number;
}) => {
  const showUnloadingCount = Number(unloading ?? 0) > 0;
  const showLoadingCount = Number(loading ?? 0) > 0;

  return (
    <Stack spacing={0.3} sx={{ width: 58, flexShrink: 0 }}>
      <Box
        sx={{
          height: 24,
          display: "grid",
          gridTemplateColumns: "22px 1fr",
          alignItems: "center",
          borderRadius: "7px",
          overflow: "hidden",
          border: `1px solid ${UNLOADING_BORDER}`,
          backgroundColor: UNLOADING_BACKGROUND,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "grid",
            placeItems: "center",
            color: UNLOADING_COLOR,
            backgroundColor: "rgba(255,255,255,0.72)",
            borderRight: `1px solid ${UNLOADING_BORDER}`,
          }}
        >
          <UnloadingIcon sx={{ fontSize: 17 }} />
        </Box>

        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 950,
            textAlign: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {showUnloadingCount ? unloading : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          height: 24,
          display: "grid",
          gridTemplateColumns: "22px 1fr",
          alignItems: "center",
          borderRadius: "7px",
          overflow: "hidden",
          border: `1px solid ${LOADING_BORDER}`,
          backgroundColor: LOADING_BACKGROUND,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "grid",
            placeItems: "center",
            color: LOADING_COLOR,
            backgroundColor: "rgba(255,255,255,0.74)",
            borderRight: `1px solid ${LOADING_BORDER}`,
          }}
        >
          <LoadingIcon sx={{ fontSize: 17 }} />
        </Box>

        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 950,
            textAlign: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {showLoadingCount ? loading : ""}
        </Typography>
      </Box>
    </Stack>
  );
};

const TerminalCallDropZone = ({
  active,
  dragging,
  onDragOver,
  onDragLeave,
  onDrop,
  t,
}: {
  active: boolean;
  dragging: boolean;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
  t: any;
}) => {
  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        height: 14,
        mx: 0.25,
        my: 0,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: dragging ? 1 : 0,
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: active ? 68 : 4,
          transform: "translateY(-50%)",
          borderRadius: WRAPPER_BORDER_RADIUS,
          border: active
            ? "2px dashed rgba(25, 118, 210, 0.75)"
            : "2px dashed transparent",
          backgroundColor: active
            ? "rgba(25, 118, 210, 0.1)"
            : "rgba(25, 118, 210, 0.08)",
          transition:
            "height 90ms ease, background-color 90ms ease, border-color 90ms ease, opacity 90ms ease",
          opacity: dragging ? 1 : 0,
          pointerEvents: "none",
        }}
      />

      {active ? (
        <Typography
          sx={{
            position: "relative",
            zIndex: 1,
            fontSize: "0.62rem",
            fontWeight: 950,
            color: "#1976d2",
            textTransform: "uppercase",
            pointerEvents: "none",
            userSelect: "none",
            lineHeight: 1,
          }}
        >
          {t("dropHere")}
        </Typography>
      ) : null}
    </Box>
  );
};

const TerminalCallListItem = ({
  terminalCall,
  selected,
  counts,
  sameDateTimeWarning = null,
  onClick,
  onDragStart,
  onDragEnd,
  dragging,
  moving,
  t,
}: {
  terminalCall: any;
  selected: boolean;
  counts: { loading: number; unloading: number };
  sameDateTimeWarning?: {
    count: number;
    dateLabel: string;
  } | null;
  onClick: () => void;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  dragging: boolean;
  moving: boolean;
  t: any;
}) => {
  const terminalColor = getTerminalCallColor(terminalCall);
  const completed = isTerminalCallCompleted(terminalCall);
  const hasSameDateTimeWarning = Boolean(sameDateTimeWarning);

  return (
    <Paper
      draggable={!moving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      elevation={0}
      sx={(theme) => ({
        position: "relative",
        minHeight: 76,
        p: 0.85,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: WRAPPER_BORDER_RADIUS,
        cursor: moving ? "not-allowed" : "grab",
        opacity: dragging ? 0.45 : 1,
        backgroundColor: hasSameDateTimeWarning
          ? "rgba(251, 191, 36, 0.08)"
          : completed
          ? alpha(theme.palette.success.main, 0.12)
          : alpha(terminalColor, selected ? 0.28 : 0.18),
        border: hasSameDateTimeWarning
          ? selected
            ? "2px solid rgba(217, 119, 6, 0.5)"
            : "1px solid rgba(217, 119, 6, 0.24)"
          : selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${alpha(terminalColor, 0.45)}`,
        boxShadow: selected
          ? hasSameDateTimeWarning
            ? "0 7px 20px rgba(217, 119, 6, 0.1)"
            : `0 7px 20px ${alpha(theme.palette.primary.main, 0.22)}`
          : hasSameDateTimeWarning
          ? "0 4px 14px rgba(217, 119, 6, 0.08)"
          : "0 4px 14px rgba(15, 23, 42, 0.08)",
        transition: "all 140ms ease",
        userSelect: "none",
        "&:active": {
          cursor: moving ? "not-allowed" : "grabbing",
        },
        "&:hover": {
          transform: moving ? "none" : "translateY(-1px)",
          boxShadow: hasSameDateTimeWarning
            ? "0 8px 20px rgba(217, 119, 6, 0.12)"
            : "0 8px 20px rgba(15, 23, 42, 0.12)",
        },
      })}
    >
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          border: hasSameDateTimeWarning
            ? "2px solid rgba(217, 119, 6, 0.42)"
            : completed
            ? `2px solid ${theme.palette.success.main}`
            : `3px solid ${terminalColor}`,
          backgroundColor: hasSameDateTimeWarning
            ? "rgba(251, 191, 36, 0.14)"
            : completed
            ? alpha(theme.palette.success.main, 0.12)
            : "rgba(255,255,255,0.72)",
          color: hasSameDateTimeWarning ? "#b45309" : "inherit",
        })}
      >
        {hasSameDateTimeWarning ? (
          <WarningAmberIcon sx={{ fontSize: 20 }} />
        ) : completed ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 21 }} />
        ) : null}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.65} alignItems="center">
          <Typography
            sx={{
              fontSize: "0.86rem",
              fontWeight: 950,
              lineHeight: 1.1,
              color: hasSameDateTimeWarning
                ? "#92400e"
                : completed
                ? "success.dark"
                : "text.primary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTerminalCallTime(terminalCall)}
          </Typography>

          {hasSameDateTimeWarning ? (
            <Tooltip
              title={t("sameTimeTerminalCallWarning", {
                count: sameDateTimeWarning?.count ?? 0,
                date: sameDateTimeWarning?.dateLabel ?? "",
                defaultValue:
                  "{{count}} terminal calls have exactly the same planned date and time: {{date}}.",
              })}
              arrow
            >
              <Chip
                size="small"
                icon={<WarningAmberIcon />}
                label={t("sameTime", "Same time")}
                sx={{
                  height: 20,
                  borderRadius: "7px",
                  backgroundColor: "rgba(251, 191, 36, 0.18)",
                  color: "#92400e",
                  border: "1px solid rgba(217, 119, 6, 0.2)",
                  fontSize: "0.56rem",
                  fontWeight: 950,
                  textTransform: "uppercase",
                  "& .MuiChip-icon": {
                    ml: 0.55,
                    mr: -0.35,
                    fontSize: "14px",
                    color: "#b45309",
                  },
                  "& .MuiChip-label": {
                    px: 0.65,
                  },
                }}
              />
            </Tooltip>
          ) : null}
        </Stack>

        <Typography
          noWrap
          sx={{
            mt: 0.3,
            fontSize: "0.78rem",
            fontWeight: 900,
            color: hasSameDateTimeWarning ? "#713f12" : "text.primary",
          }}
        >
          {getTerminalCallNumber(terminalCall)}
        </Typography>

        <Typography
          noWrap
          sx={{
            mt: 0.2,
            fontSize: "0.68rem",
            fontWeight: 700,
            color: hasSameDateTimeWarning ? "#a16207" : "text.secondary",
          }}
        >
          {getTerminalName(terminalCall, t)}
        </Typography>
      </Box>

      <TerminalCallStatusBadge terminalCall={terminalCall} t={t} />

      <TerminalCallLoadCounts
        loading={counts.loading}
        unloading={counts.unloading}
      />
    </Paper>
  );
};

const TerminalCallOptionCard = ({
  terminalCall,
  selected,
  searchTerm = "",
  t,
}: {
  terminalCall: any;
  selected: boolean;
  searchTerm?: string;
  t: any;
}) => {
  const terminalColor = getTerminalCallColor(terminalCall);
  const completed = isTerminalCallCompleted(terminalCall);
  const loadingCount = Array.isArray(terminalCall?.loadingContainers)
    ? terminalCall.loadingContainers.length
    : 0;
  const unloadingCount = Array.isArray(terminalCall?.unloadingContainers)
    ? terminalCall.unloadingContainers.length
    : 0;

  return (
    <Box
      sx={(theme) => ({
        width: "100%",
        minHeight: 82,
        p: 0.85,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: WRAPPER_BORDER_RADIUS,
        backgroundColor: completed
          ? alpha(theme.palette.success.main, 0.12)
          : alpha(terminalColor, selected ? 0.28 : 0.18),
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${alpha(terminalColor, 0.45)}`,
        boxShadow: selected
          ? `0 7px 20px ${alpha(theme.palette.primary.main, 0.18)}`
          : "0 4px 14px rgba(15, 23, 42, 0.08)",
        transition: "all 140ms ease",
      })}
    >
      <Box
        sx={(theme) => ({
          width: 30,
          height: 30,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          border: completed
            ? `2px solid ${theme.palette.success.main}`
            : selected
            ? `2px solid ${theme.palette.primary.main}`
            : `3px solid ${terminalColor}`,
          backgroundColor: completed
            ? alpha(theme.palette.success.main, 0.12)
            : selected
            ? alpha(theme.palette.primary.main, 0.1)
            : "rgba(255,255,255,0.72)",
        })}
      >
        {completed ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 22 }} />
        ) : selected ? (
          <CheckCircleIcon color="primary" sx={{ fontSize: 21 }} />
        ) : null}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 950,
              lineHeight: 1.1,
              color: completed ? "success.dark" : "text.primary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTerminalCallFullDate(terminalCall, t)}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.65} sx={{ mt: 0.35 }}>
          <TerminalIcon sx={{ fontSize: 15, color: "#1976d2", flexShrink: 0 }} />

          <Typography
            noWrap
            sx={{
              fontSize: "0.76rem",
              fontWeight: 850,
              color: "text.primary",
            }}
          >
            <HighlightedText
              value={getTerminalName(terminalCall, t)}
              searchTerm={searchTerm}
            />
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.65} sx={{ mt: 0.45 }}>
          <CalendarMonthIcon
            sx={{ fontSize: 16, color: "#1976d2", flexShrink: 0 }}
          />

          <Typography
            noWrap
            sx={{
              fontSize: "0.76rem",
              fontWeight: 950,
              color: "#1d4ed8",
            }}
          >
            <HighlightedText
              value={formatTerminalCallFullDate(terminalCall, t)}
              searchTerm={searchTerm}
            />
          </Typography>
        </Stack>
      </Box>

      <TerminalCallStatusBadge terminalCall={terminalCall} t={t} />

      <TerminalCallLoadCounts loading={loadingCount} unloading={unloadingCount} />
    </Box>
  );
};

const DetailedTerminalCallChoice = ({
  terminalCall,
  selected,
  searchTerm = "",
  t,
}: {
  terminalCall: any;
  selected: boolean;
  searchTerm?: string;
  t: any;
}) => {
  return (
    <TerminalCallOptionCard
      terminalCall={terminalCall}
      selected={selected}
      searchTerm={searchTerm}
      t={t}
    />
  );
};

const SelectedTerminalCallValue = ({
  terminalCall,
  resolvedTerminal,
  type,
  searchTerm = "",
  t,
}: {
  terminalCall?: any;
  resolvedTerminal?: any;
  type: "loading" | "unloading";
  searchTerm?: string;
  t: any;
}) => {
  const isLoading = type === "loading";
  const modeColor = isLoading ? LOADING_COLOR : UNLOADING_COLOR;
  const terminalName = terminalCall
    ? getTerminalName(terminalCall, t)
    : getTerminalNameFromTerminal(resolvedTerminal, t);

  if (!terminalCall && !resolvedTerminal) {
    return (
      <Box
        sx={{
          minHeight: 48,
          px: 1,
          py: 0.65,
          display: "flex",
          alignItems: "center",
          gap: 0.85,
        }}
      >
        {isLoading ? (
          <LoadingIcon sx={{ fontSize: 18, color: modeColor }} />
        ) : (
          <UnloadingIcon sx={{ fontSize: 18, color: modeColor }} />
        )}

        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: "0.78rem",
              fontWeight: 950,
              color: modeColor,
              textTransform: "uppercase",
              lineHeight: 1.1,
            }}
          >
            {isLoading
              ? t("loadingTerminalCallColumn")
              : t("unloadingTerminalCallColumn")}
          </Typography>

          <Typography
            noWrap
            sx={{
              mt: 0.25,
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "text.secondary",
            }}
          >
            {t("selectTerminalCall")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 48,
        px: 1,
        py: 0.65,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <TerminalIcon sx={{ fontSize: 16, color: modeColor }} />

          <Typography
            noWrap
            sx={{
              fontSize: "0.8rem",
              fontWeight: 950,
              color: "text.primary",
            }}
          >
            <HighlightedText value={terminalName} searchTerm={searchTerm} />
          </Typography>

          {terminalCall ? (
            <TerminalCallStatusBadge terminalCall={terminalCall} t={t} />
          ) : (
            <Chip
              size="small"
              label={t("notPlannedYet")}
              sx={{
                height: 19,
                fontSize: "0.58rem",
                fontWeight: 950,
                textTransform: "uppercase",
                backgroundColor: "#fef9c3",
                color: "#a16207",
              }}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 0.45 }}
        >
          <CalendarMonthIcon sx={{ fontSize: 15, color: "#1976d2" }} />

          <Typography
            noWrap
            sx={{
              fontSize: "0.72rem",
              color: "#1d4ed8",
              fontWeight: 900,
            }}
          >
            <HighlightedText
              value={
                terminalCall
                  ? formatContainerDateTime(getTerminalCallDateValue(terminalCall), t)
                  : t("noTerminalCallAssigned")
              }
              searchTerm={searchTerm}
            />
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

const ContainerTerminalCallCard = ({
  container,
  type,
  terminalCall,
  terminal,
  terminalCallOptions,
  searchTerm = "",
  disabled = false,
  onRequestChange,
  t,
}: {
  container: any;
  type: "loading" | "unloading";
  terminalCall?: any;
  terminal?: any;
  terminalCallOptions: any[];
  searchTerm?: string;
  disabled?: boolean;
  onRequestChange: (payload: {
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  }) => void;
  t: any;
}) => {
  const currentTerminalCallId = getEntityId(terminalCall);
  const resolvedTerminal = terminalCall?.terminal || terminal;

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <Select
        displayEmpty
        disabled={disabled}
        value={currentTerminalCallId || ""}
        onChange={(event) => {
          if (disabled) {
            return;
          }

          const nextTerminalCallId = String(event.target.value || "");

          if (!nextTerminalCallId || nextTerminalCallId === currentTerminalCallId) {
            return;
          }

          const nextTerminalCall = terminalCallOptions.find(
            (option: any) => getEntityId(option) === nextTerminalCallId
          );

          if (!nextTerminalCall) {
            return;
          }

          onRequestChange({
            container,
            type,
            terminalCall: nextTerminalCall,
          });
        }}
        renderValue={() => (
          <SelectedTerminalCallValue
            terminalCall={terminalCall}
            resolvedTerminal={resolvedTerminal}
            type={type}
            searchTerm={searchTerm}
            t={t}
          />
        )}
        sx={{
          minHeight: 52,
          borderRadius: "10px",
          backgroundColor: disabled ? "rgba(15, 23, 42, 0.035)" : "#ffffff",
          transition:
            "box-shadow 120ms ease, background-color 120ms ease, border-color 120ms ease",
          "& .MuiSelect-select": {
            p: 0,
          },
          "& fieldset": {
            borderColor: "transparent",
          },
          "&:hover fieldset": {
            borderColor: disabled
              ? "transparent !important"
              : "rgba(25, 118, 210, 0.35) !important",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#1976d2 !important",
            borderWidth: "1px !important",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.08)",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              width: 480,
              maxHeight: 460,
              p: 0.75,
              borderRadius: "12px",
            },
          },
        }}
      >
        {terminalCallOptions.map((option: any) => {
          const optionId = getEntityId(option);
          const selected = optionId === currentTerminalCallId;

          return (
            <MenuItem
              key={optionId}
              value={optionId}
              sx={{
                p: 0.35,
                mb: 0.55,
                borderRadius: "12px",
                whiteSpace: "normal",
                "&.Mui-selected": {
                  backgroundColor: "transparent !important",
                },
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.05)",
                },
              }}
            >
              <DetailedTerminalCallChoice
                terminalCall={option}
                selected={selected}
                searchTerm={searchTerm}
                t={t}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const BulkTerminalCallSelect = ({
  type,
  value,
  currentTerminalCall,
  currentTerminalCallId,
  terminalCallOptions,
  disabled = false,
  onChange,
  t,
}: {
  type: "loading" | "unloading";
  value: string;
  currentTerminalCall: any;
  currentTerminalCallId: string;
  terminalCallOptions: any[];
  disabled?: boolean;
  onChange: (value: string) => void;
  t: any;
}) => {
  const selectedTerminalCall = value
    ? terminalCallOptions.find(
        (terminalCall: any) => getEntityId(terminalCall) === value
      )
    : null;

  const isLoading = type === "loading";
  const modeColor = isLoading ? LOADING_COLOR : UNLOADING_COLOR;
  const modeBackground = isLoading ? LOADING_BACKGROUND : UNLOADING_BACKGROUND;
  const modeBorder = isLoading ? LOADING_BORDER : UNLOADING_BORDER;
  const placeholder = isLoading
    ? t("loadingTerminalCallColumn")
    : t("unloadingTerminalCallColumn");

  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <Select
        displayEmpty
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const nextValue = String(event.target.value || "");

          if (nextValue === currentTerminalCallId) {
            return;
          }

          onChange(nextValue);
        }}
        renderValue={() => {
          if (!selectedTerminalCall) {
            return (
              <Box
                sx={{
                  minHeight: 48,
                  px: 1,
                  py: 0.65,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.85,
                }}
              >
                {isLoading ? (
                  <LoadingIcon sx={{ fontSize: 18, color: modeColor }} />
                ) : (
                  <UnloadingIcon sx={{ fontSize: 18, color: modeColor }} />
                )}

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 950,
                      color: modeColor,
                      textTransform: "uppercase",
                      lineHeight: 1.1,
                    }}
                  >
                    {placeholder}
                  </Typography>

                  <Typography
                    noWrap
                    sx={{
                      mt: 0.25,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "text.secondary",
                    }}
                  >
                    {t("selectTerminalCall")}
                  </Typography>
                </Box>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                minHeight: 48,
                px: 1,
                py: 0.65,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TerminalIcon sx={{ fontSize: 16, color: modeColor }} />

                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 950,
                      color: "text.primary",
                    }}
                  >
                    {getTerminalName(selectedTerminalCall, t)}
                  </Typography>

                  <TerminalCallStatusBadge terminalCall={selectedTerminalCall} t={t} />
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ mt: 0.45 }}
                >
                  <CalendarMonthIcon sx={{ fontSize: 15, color: "#1976d2" }} />

                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.72rem",
                      color: "#1d4ed8",
                      fontWeight: 900,
                    }}
                  >
                    {getTerminalCallOptionLabel(selectedTerminalCall, t)}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          );
        }}
        sx={{
          minHeight: 52,
          borderRadius: "10px",
          backgroundColor: disabled ? "rgba(15, 23, 42, 0.035)" : modeBackground,
          transition:
            "box-shadow 120ms ease, background-color 120ms ease, border-color 120ms ease",
          "& .MuiSelect-select": {
            p: 0,
          },
          "& fieldset": {
            borderColor: modeBorder,
          },
          "&:hover fieldset": {
            borderColor: disabled
              ? `${modeBorder} !important`
              : `${alpha(modeColor, 0.45)} !important`,
          },
          "&.Mui-focused fieldset": {
            borderColor: `${modeColor} !important`,
            borderWidth: "1px !important",
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(modeColor, 0.1)}`,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              width: 480,
              maxHeight: 460,
              p: 0.75,
              borderRadius: "12px",
            },
          },
        }}
      >
        <MenuItem value="">{placeholder}</MenuItem>

        {currentTerminalCall ? (
          <CurrentTerminalCallMenuItem
            value={currentTerminalCallId}
            terminalCall={currentTerminalCall}
            t={t}
          />
        ) : null}

        {terminalCallOptions
          .filter(
            (terminalCall: any) => getEntityId(terminalCall) !== currentTerminalCallId
          )
          .map((terminalCall: any) => {
            const terminalCallId = getEntityId(terminalCall);
            const selected = terminalCallId === value;

            return (
              <MenuItem
                key={terminalCallId}
                value={terminalCallId}
                sx={{
                  p: 0.35,
                  mb: 0.55,
                  borderRadius: "12px",
                  whiteSpace: "normal",
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.05)",
                  },
                }}
              >
                <DetailedTerminalCallChoice
                  terminalCall={terminalCall}
                  selected={selected}
                  t={t}
                />
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
};

const ContainerRow = ({
  container,
  checked,
  onToggle,
  terminalCallOptions,
  onRequestTerminalCallChange,
  searchTerm = "",
  mode,
  disabled = false,
  t,
}: {
  container: any;
  checked: boolean;
  onToggle: () => void;
  terminalCallOptions: any[];
  onRequestTerminalCallChange: (payload: {
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  }) => void;
  searchTerm?: string;
  mode: TerminalCallMode;
  disabled?: boolean;
  t: any;
}) => {
  const loadingHighlighted = mode === "loading";
  const unloadingHighlighted = mode === "unloading";
  const positionedOnVessel = isContainerPositionedOnVessel(container);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          "44px minmax(230px, 0.9fr) minmax(300px, 1fr) minmax(80px, 0.22fr) minmax(300px, 1fr) minmax(90px, 0.28fr) 42px",
        alignItems: "stretch",
        borderTop: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: checked ? "rgba(25, 118, 210, 0.035)" : "#ffffff",
        opacity: disabled ? 0.82 : 1,
      }}
    >
      <Box sx={{ display: "grid", placeItems: "center" }}>
        <Checkbox
          checked={checked}
          onChange={onToggle}
          size="small"
          disabled={disabled}
        />
      </Box>

      <Box
        sx={{
          px: 1.25,
          py: 1.2,
          minWidth: 0,
          backgroundColor: positionedOnVessel
            ? "rgba(14, 165, 233, 0.035)"
            : "transparent",
          borderLeft: positionedOnVessel
            ? "1px solid rgba(14, 165, 233, 0.1)"
            : "1px solid transparent",
          borderRight: positionedOnVessel
            ? "1px solid rgba(14, 165, 233, 0.1)"
            : "1px solid transparent",
          transition: "background-color 120ms ease, border-color 120ms ease",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <ContainerTypeBadge container={container} searchTerm={searchTerm} />

          <FileIcon sx={{ fontSize: 15, color: UNLOADING_COLOR, flexShrink: 0 }} />

          <Typography
            noWrap
            sx={{
              fontSize: "0.92rem",
              fontWeight: 950,
              color: "text.primary",
              maxWidth: "100%",
            }}
          >
            <HighlightedText
              value={getContainerDisplayLabel(container, t)}
              searchTerm={searchTerm}
            />
          </Typography>

          <PositionedOnVesselBadge container={container} t={t} />
          <ContainerPositionChip container={container} t={t} />
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 0.55 }}
        >
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: positionedOnVessel ? "#64748b" : "text.secondary",
              fontWeight: 700,
            }}
          >
            {formatWeightKg(container?.weight)} kg
          </Typography>

          {positionedOnVessel ? (
            <Typography
              noWrap
              sx={{
                fontSize: "0.72rem",
                color: "#64748b",
                fontWeight: 800,
                maxWidth: 150,
              }}
            >
              {getPositionedVesselLabel(container, t)}
            </Typography>
          ) : null}
        </Stack>
      </Box>

      <Box
        sx={{
          px: 0.75,
          py: 0.5,
          backgroundColor: loadingHighlighted ? LOADING_BACKGROUND : "transparent",
          borderLeft: loadingHighlighted
            ? `1px solid ${LOADING_BORDER}`
            : "1px solid transparent",
          borderRight: loadingHighlighted
            ? `1px solid ${LOADING_BORDER}`
            : "1px solid transparent",
        }}
      >
        <ContainerTerminalCallCard
          container={container}
          type="loading"
          terminalCall={container?.loadingTerminalCall}
          terminal={container?.loadingTerminal}
          terminalCallOptions={terminalCallOptions}
          searchTerm={searchTerm}
          disabled={disabled}
          onRequestChange={onRequestTerminalCallChange}
          t={t}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          color: "text.primary",
          fontWeight: 950,
          fontSize: "1.25rem",
          lineHeight: 1,
        }}
      >
        {mode === "unloading" ? "←" : "→"}
      </Box>

      <Box
        sx={{
          px: 0.75,
          py: 0.5,
          backgroundColor: unloadingHighlighted
            ? UNLOADING_BACKGROUND
            : "transparent",
          borderLeft: unloadingHighlighted
            ? `1px solid ${UNLOADING_BORDER}`
            : "1px solid transparent",
          borderRight: unloadingHighlighted
            ? `1px solid ${UNLOADING_BORDER}`
            : "1px solid transparent",
        }}
      >
        <ContainerTerminalCallCard
          container={container}
          type="unloading"
          terminalCall={container?.unloadingTerminalCall}
          terminal={container?.unloadingTerminal}
          terminalCallOptions={terminalCallOptions}
          searchTerm={searchTerm}
          disabled={disabled}
          onRequestChange={onRequestTerminalCallChange}
          t={t}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          px: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.82rem",
            fontWeight: 850,
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          {formatWeightKg(container?.weight)} kg
        </Typography>
      </Box>

      <Box sx={{ display: "grid", placeItems: "center" }}>
        <IconButton size="small" disabled={disabled}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const TrajectoryTerminalBlock = ({
  terminalCall,
  fallbackLabel,
  align = "left",
  searchTerm = "",
  t,
}: {
  terminalCall: any;
  fallbackLabel: string;
  align?: "left" | "right";
  searchTerm?: string;
  t: any;
}) => {
  const terminalName = terminalCall ? getTerminalName(terminalCall, t) : fallbackLabel;

  return (
    <Box sx={{ minWidth: 0, textAlign: align }}>
      <Typography
        noWrap
        sx={{
          fontSize: "0.86rem",
          fontWeight: 950,
          color: "text.primary",
          lineHeight: 1.15,
        }}
      >
        <HighlightedText value={terminalName} searchTerm={searchTerm} />
      </Typography>

      <Typography
        noWrap
        sx={{
          mt: 0.3,
          fontSize: "0.72rem",
          fontWeight: 950,
          color: "#1d4ed8",
        }}
      >
        <HighlightedText
          value={formatTerminalCallFullDate(terminalCall, t)}
          searchTerm={searchTerm}
        />
      </Typography>
    </Box>
  );
};

const TrajectoryRouteSummary = ({
  group,
  mode,
  searchTerm = "",
  t,
}: {
  group: any;
  mode: TerminalCallMode;
  searchTerm?: string;
  t: any;
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 1fr) 32px minmax(120px, 1fr)",
        alignItems: "center",
        gap: 0.8,
        minWidth: 0,
        width: "min(620px, 100%)",
      }}
    >
      <TrajectoryTerminalBlock
        terminalCall={group.loadingTerminalCall}
        fallbackLabel={group.loadingLabel}
        searchTerm={searchTerm}
        t={t}
      />

      <Typography
        sx={{
          textAlign: "center",
          fontSize: "1rem",
          fontWeight: 950,
          color: mode === "loading" ? LOADING_COLOR : UNLOADING_COLOR,
          lineHeight: 1,
        }}
      >
        {mode === "unloading" ? "←" : "→"}
      </Typography>

      <TrajectoryTerminalBlock
        terminalCall={group.unloadingTerminalCall}
        fallbackLabel={group.unloadingLabel}
        align="right"
        searchTerm={searchTerm}
        t={t}
      />
    </Box>
  );
};

const TrajectoryGroup = ({
  group,
  selectedContainerIds,
  onToggleContainer,
  onToggleGroup,
  terminalCallOptions,
  onRequestTerminalCallChange,
  initiallyExpanded = true,
  forceExpanded = false,
  searchTerm = "",
  mode,
  disabled = false,
  t,
}: {
  group: any;
  selectedContainerIds: string[];
  onToggleContainer: (container: any) => void;
  onToggleGroup: (containers: any[]) => void;
  terminalCallOptions: any[];
  onRequestTerminalCallChange: (payload: {
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  }) => void;
  initiallyExpanded?: boolean;
  forceExpanded?: boolean;
  searchTerm?: string;
  mode: TerminalCallMode;
  disabled?: boolean;
  t: any;
}) => {
  const [expanded, setExpanded] = useState(forceExpanded || initiallyExpanded);
  const [showAllContainers, setShowAllContainers] = useState(false);

  useEffect(() => {
    setExpanded(forceExpanded || initiallyExpanded);
    setShowAllContainers(false);
  }, [group.key, initiallyExpanded, forceExpanded]);

  const hasSearchTerm = searchTerm.trim().length > 0;

  const visibleContainers = expanded
    ? showAllContainers || hasSearchTerm || forceExpanded
      ? group.containers
      : group.containers.slice(0, 5)
    : [];

  const groupContainerIds = group.containers.map(getEntityId).filter(Boolean);
  const selectedGroupCount = groupContainerIds.filter((id: string) =>
    selectedContainerIds.includes(id)
  ).length;

  const selectedAllGroup =
    groupContainerIds.length > 0 && selectedGroupCount === groupContainerIds.length;

  const selectedSomeGroup =
    selectedGroupCount > 0 && selectedGroupCount < groupContainerIds.length;

  const remaining = Math.max(group.containers.length - visibleContainers.length, 0);

  const toggleExpanded = () => {
    setExpanded((current) => {
      const nextExpanded = !current;

      if (!nextExpanded) {
        setShowAllContainers(false);
      }

      return nextExpanded;
    });
  };

  const showRemainingContainers = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setExpanded(true);
    setShowAllContainers(true);
  };

  const showLessContainers = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setShowAllContainers(false);
  };

  const modeColor = mode === "loading" ? LOADING_COLOR : UNLOADING_COLOR;
  const modeBackground =
    mode === "loading" ? LOADING_BACKGROUND : UNLOADING_BACKGROUND;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: WRAPPER_BORDER_RADIUS,
        overflow: "hidden",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 5px 18px rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
          alignItems: "center",
          gap: 1.15,
          pl: "12px",
          pr: 2,
          py: 1.1,
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          backgroundColor: "#ffffff",
        }}
      >
        <Checkbox
          checked={selectedAllGroup}
          indeterminate={selectedSomeGroup}
          onChange={() => onToggleGroup(group.containers)}
          size="small"
          disabled={disabled}
          sx={{ p: 0.25 }}
        />

        <TrajectoryRouteSummary
          group={group}
          mode={mode}
          searchTerm={searchTerm}
          t={t}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={group.containers.length}
            sx={{
              height: 23,
              fontSize: "0.72rem",
              fontWeight: 950,
              backgroundColor: modeBackground,
              color: modeColor,
            }}
          />

          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 800,
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            {t("totalWeightLabel", { weight: formatWeightKg(group.totalWeight) })}
          </Typography>

          <IconButton size="small" onClick={toggleExpanded}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        <IconButton size="small" disabled={disabled}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {expanded ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "44px minmax(230px, 0.9fr) minmax(300px, 1fr) minmax(80px, 0.22fr) minmax(300px, 1fr) minmax(90px, 0.28fr) 42px",
              alignItems: "center",
              minHeight: 46,
              backgroundColor: "#fbfdff",
              color: "text.secondary",
              borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box />

            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800 }}>
              {t("container")}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 900,
                color: mode === "loading" ? LOADING_COLOR : "text.secondary",
                backgroundColor:
                  mode === "loading" ? LOADING_BACKGROUND : "transparent",
                height: "100%",
                display: "flex",
                alignItems: "center",
                px: 1,
              }}
            >
              {mode === "loading"
                ? t("loadingTerminalCallColumn")
                : t("loadingTerminalCallFromColumn")}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              {t("trajectory")}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 900,
                color: mode === "unloading" ? UNLOADING_COLOR : "text.secondary",
                backgroundColor:
                  mode === "unloading" ? UNLOADING_BACKGROUND : "transparent",
                height: "100%",
                display: "flex",
                alignItems: "center",
                px: 1,
              }}
            >
              {mode === "unloading"
                ? t("unloadingTerminalCallColumn")
                : t("unloadingTerminalCallToColumn")}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 800,
                textAlign: "right",
                pr: 1,
              }}
            >
              {t("weight")}
            </Typography>

            <Box />
          </Box>

          {visibleContainers.map((container: any) => {
            const containerId = getEntityId(container);

            return (
              <ContainerRow
                key={containerId || container.uid}
                container={container}
                checked={selectedContainerIds.includes(containerId)}
                onToggle={() => onToggleContainer(container)}
                terminalCallOptions={terminalCallOptions}
                onRequestTerminalCallChange={onRequestTerminalCallChange}
                searchTerm={searchTerm}
                mode={mode}
                disabled={disabled}
                t={t}
              />
            );
          })}

          {!hasSearchTerm && !showAllContainers && !forceExpanded && remaining > 0 ? (
            <Box
              onClick={showRemainingContainers}
              sx={{
                height: 46,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                color: modeColor,
                cursor: "pointer",
                fontWeight: 850,
                fontSize: "0.86rem",
                "&:hover": {
                  backgroundColor: modeBackground,
                },
              }}
            >
              {t("showMoreContainers", { count: remaining })}
              <ExpandMoreIcon sx={{ ml: 0.5, fontSize: 19 }} />
            </Box>
          ) : null}

          {!hasSearchTerm &&
          showAllContainers &&
          !forceExpanded &&
          group.containers.length > 5 ? (
            <Box
              onClick={showLessContainers}
              sx={{
                height: 46,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                color: modeColor,
                cursor: "pointer",
                fontWeight: 850,
                fontSize: "0.86rem",
                "&:hover": {
                  backgroundColor: modeBackground,
                },
              }}
            >
              {t("showLess")}
              <ExpandLessIcon sx={{ ml: 0.5, fontSize: 19 }} />
            </Box>
          ) : null}
        </>
      ) : null}
    </Paper>
  );
};

const ContainerSection = ({
  mode,
  groups,
  selectedContainerIds,
  onToggleContainer,
  onToggleGroup,
  terminalCallOptions,
  onRequestTerminalCallChange,
  searchTerm,
  forceExpanded = false,
  disabled = false,
  t,
}: {
  mode: TerminalCallMode;
  groups: any[];
  selectedContainerIds: string[];
  onToggleContainer: (container: any) => void;
  onToggleGroup: (containers: any[]) => void;
  terminalCallOptions: any[];
  onRequestTerminalCallChange: (payload: {
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  }) => void;
  searchTerm: string;
  forceExpanded?: boolean;
  disabled?: boolean;
  t: any;
}) => {
  const totalContainers = groups.reduce(
    (total, group) => total + group.containers.length,
    0
  );
  const totalWeight = groups.reduce((total, group) => total + group.totalWeight, 0);

  const isLoading = mode === "loading";
  const modeBackground = isLoading ? LOADING_BACKGROUND : UNLOADING_BACKGROUND;
  const modeBorder = isLoading ? LOADING_BORDER : UNLOADING_BORDER;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: WRAPPER_BORDER_RADIUS,
        overflow: "hidden",
        border: `1px solid ${modeBorder}`,
        backgroundColor: isLoading
          ? "rgba(234, 179, 8, 0.04)"
          : "rgba(22, 163, 74, 0.045)",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.3,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "center",
          gap: 2,
          borderBottom: `1px solid ${modeBorder}`,
          backgroundColor: modeBackground,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2} minWidth={0}>
          {isLoading ? (
            <LoadingIcon sx={{ color: LOADING_COLOR, fontSize: 25 }} />
          ) : (
            <UnloadingIcon sx={{ color: UNLOADING_COLOR, fontSize: 25 }} />
          )}

          <Typography
            noWrap
            sx={{
              fontSize: "1rem",
              fontWeight: 950,
              color: "text.primary",
              textTransform: "uppercase",
            }}
          >
            {isLoading
              ? t("loadingContainersOnThisTerminalCall")
              : t("unloadingContainersOnThisTerminalCall")}
          </Typography>

          <Chip
            size="small"
            label={t("containersCount", { count: totalContainers })}
            sx={{
              height: 22,
              fontSize: "0.68rem",
              fontWeight: 950,
              backgroundColor: isLoading ? "#fef9c3" : "#dcfce7",
              color: isLoading ? "#a16207" : "#15803d",
            }}
          />
        </Stack>

        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 850,
            color: "text.secondary",
          }}
        >
          {t("totalWeightLabel", { weight: formatWeightKg(totalWeight) })}
        </Typography>
      </Box>

      <Box sx={{ p: 1 }}>
        <Stack spacing={1.25}>
          {groups.map((group: any, index: number) => (
            <TrajectoryGroup
              key={`${mode}-${group.key}`}
              group={group}
              selectedContainerIds={selectedContainerIds}
              onToggleContainer={onToggleContainer}
              onToggleGroup={onToggleGroup}
              terminalCallOptions={terminalCallOptions}
              onRequestTerminalCallChange={onRequestTerminalCallChange}
              initiallyExpanded={index < 2}
              forceExpanded={forceExpanded}
              searchTerm={searchTerm}
              mode={mode}
              disabled={disabled}
              t={t}
            />
          ))}

          {groups.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: WRAPPER_BORDER_RADIUS,
                border: "1px solid rgba(15, 23, 42, 0.08)",
                textAlign: "center",
                backgroundColor: "#ffffff",
              }}
            >
              <Typography fontWeight={900}>
                {isLoading
                  ? t("noLoadingContainersFound")
                  : t("noUnloadingContainersFound")}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "0.86rem",
                }}
              >
                {t("selectAnotherTerminalCallOrClearSearch")}
              </Typography>
            </Paper>
          ) : null}
        </Stack>
      </Box>
    </Paper>
  );
};

const SearchResultsSection = ({
  groups,
  selectedContainerIds,
  onToggleContainer,
  onToggleGroup,
  terminalCallOptions,
  onRequestTerminalCallChange,
  searchTerm,
  disabled = false,
  t,
}: {
  groups: any[];
  selectedContainerIds: string[];
  onToggleContainer: (container: any) => void;
  onToggleGroup: (containers: any[]) => void;
  terminalCallOptions: any[];
  onRequestTerminalCallChange: (payload: {
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  }) => void;
  searchTerm: string;
  disabled?: boolean;
  t: any;
}) => {
  const totalContainers = groups.reduce(
    (total, group) => total + group.containers.length,
    0
  );
  const totalWeight = groups.reduce((total, group) => total + group.totalWeight, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: WRAPPER_BORDER_RADIUS,
        overflow: "hidden",
        border: `1px solid ${SEARCH_BORDER}`,
        backgroundColor: "rgba(37, 99, 235, 0.035)",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.3,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "center",
          gap: 2,
          borderBottom: `1px solid ${SEARCH_BORDER}`,
          backgroundColor: SEARCH_BACKGROUND,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2} minWidth={0}>
          <SearchIcon sx={{ color: SEARCH_COLOR, fontSize: 24 }} />

          <Typography
            noWrap
            sx={{
              fontSize: "1rem",
              fontWeight: 950,
              color: "text.primary",
              textTransform: "uppercase",
            }}
          >
            {t("searchResults")}
          </Typography>

          <Chip
            size="small"
            label={t("containersCount", { count: totalContainers })}
            sx={{
              height: 22,
              fontSize: "0.68rem",
              fontWeight: 950,
              backgroundColor: "#dbeafe",
              color: SEARCH_COLOR,
            }}
          />
        </Stack>

        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 850,
            color: "text.secondary",
          }}
        >
          {t("totalWeightLabel", { weight: formatWeightKg(totalWeight) })}
        </Typography>
      </Box>

      <Box sx={{ p: 1 }}>
        <Stack spacing={1.25}>
          {groups.map((group: any, index: number) => (
            <TrajectoryGroup
              key={`search-${group.key}`}
              group={group}
              selectedContainerIds={selectedContainerIds}
              onToggleContainer={onToggleContainer}
              onToggleGroup={onToggleGroup}
              terminalCallOptions={terminalCallOptions}
              onRequestTerminalCallChange={onRequestTerminalCallChange}
              initiallyExpanded={index < 4}
              forceExpanded={false}
              searchTerm={searchTerm}
              mode="loading"
              disabled={disabled}
              t={t}
            />
          ))}

          {groups.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: WRAPPER_BORDER_RADIUS,
                border: "1px solid rgba(15, 23, 42, 0.08)",
                textAlign: "center",
                backgroundColor: "#ffffff",
              }}
            >
              <Typography fontWeight={900}>{t("noContainersFound")}</Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "0.86rem",
                }}
              >
                {t("selectAnotherTerminalCallOrClearSearch")}
              </Typography>
            </Paper>
          ) : null}
        </Stack>
      </Box>
    </Paper>
  );
};

const CurrentTerminalCallMenuItem = ({
  terminalCall,
  value,
  t,
}: {
  terminalCall: any;
  value: string;
  t: any;
}) => {
  return (
    <MenuItem
      value={value}
      disabled
      sx={{
        position: "relative",
        pr: 9,
        opacity: "1 !important",
        backgroundColor: "#fef9c3 !important",
        color: "#713f12",
        fontWeight: 900,
        "&.Mui-disabled": {
          opacity: "1 !important",
          color: "#713f12",
          WebkitTextFillColor: "#713f12",
        },
      }}
    >
      {getTerminalCallOptionLabel(terminalCall, t)}

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: 10,
          transform: "translateY(-50%)",
          px: 0.75,
          py: 0.2,
          borderRadius: "999px",
          backgroundColor: "#facc15",
          color: "#713f12",
          fontSize: "0.62rem",
          fontWeight: 950,
          lineHeight: 1,
          textTransform: "uppercase",
          pointerEvents: "none",
        }}
      >
        {t("current")}
      </Box>
    </MenuItem>
  );
};

const ChangeSummaryRow = ({
  title,
  currentValue,
  nextValue,
  t,
}: {
  title: string;
  currentValue: string;
  nextValue: string;
  t: any;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: "10px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#f8fafc",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 950,
          color: "text.primary",
          mb: 0.75,
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "text.secondary",
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            {t("current")}
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              fontSize: "0.82rem",
              fontWeight: 850,
              color: "text.primary",
            }}
          >
            {currentValue}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 950,
            color: "#1976d2",
          }}
        >
          →
        </Typography>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "text.secondary",
              fontWeight: 800,
              textTransform: "uppercase",
            }}
          >
            {t("new")}
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              fontSize: "0.82rem",
              fontWeight: 950,
              color: "#1976d2",
            }}
          >
            {nextValue}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const VoyageTerminalCallContainerManager = () => {
  useInitialPlannerData();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const refreshVoyageData = useRefreshVoyageData();

  const { voyage } = useSelector(selectedFilteredVoyage);

  const {
    selectedTerminalCallsState,
    selectedContainersState,
    selectedTerminalCallFilterValue,
  } = useSelector((state: any) => ({
    selectedTerminalCallsState: state.selectedTerminalCalls,
    selectedContainersState: state.selectedContainers,
    selectedTerminalCallFilterValue: state.filter.selectedTerminalCall,
  }));

  const {
    terminalCalls,
    groupedTerminalCallsByDate,
    terminalCallCountsById,
    terminalCallSameDateTimeWarnings,
    loadingTrajectories,
    unloadingTrajectories,
    allEnrichedContainers,
    selectedTerminalCall,
    loading,
  } = useMemo(() => {
    const rawTerminalCalls = normalizeCollection(selectedTerminalCallsState?.data);
    const rawContainers = normalizeCollection(selectedContainersState?.data);

    const terminalCalls = sortTerminalCallsByDate(rawTerminalCalls);

    const terminalCallMap = new Map<string, any>();

    terminalCalls.forEach((terminalCall: any) => {
      getComparableIds(terminalCall).forEach((id) => {
        terminalCallMap.set(id, terminalCall);
      });
    });

    const containerMap = new Map<string, any>();

    rawContainers.forEach((container: any) => {
      getComparableIds(container).forEach((id) => {
        containerMap.set(id, container);
      });
    });

    const groupedTerminalCallsByDateMap = new Map<string, any[]>();

    terminalCalls.forEach((terminalCall: any) => {
      const date = getTerminalCallDate(terminalCall);
      const key = date?.isValid() ? date.format("DD/MM/YYYY") : t("noDate");

      if (!groupedTerminalCallsByDateMap.has(key)) {
        groupedTerminalCallsByDateMap.set(key, []);
      }

      groupedTerminalCallsByDateMap.get(key)?.push(terminalCall);
    });

    const groupedTerminalCallsByDate = Array.from(
      groupedTerminalCallsByDateMap.entries()
    )
      .map(([date, calls]) => ({
        date,
        calls: sortTerminalCallsByDate(calls),
      }))
      .sort((a, b) => {
        if (a.date === t("noDate")) return 1;
        if (b.date === t("noDate")) return -1;

        return (
          dayjs(a.date, "DD/MM/YYYY").valueOf() -
          dayjs(b.date, "DD/MM/YYYY").valueOf()
        );
      });

    const terminalCallCountsById: any = {};

    terminalCalls.forEach((terminalCall: any) => {
      const terminalCallId = normalizeApiId(terminalCall);

      if (!terminalCallId) {
        return;
      }

      terminalCallCountsById[terminalCallId] = {
        loading: Array.isArray(terminalCall?.loadingContainers)
          ? terminalCall.loadingContainers.length
          : 0,
        unloading: Array.isArray(terminalCall?.unloadingContainers)
          ? terminalCall.unloadingContainers.length
          : 0,
      };
    });

    const terminalCallsByDateTime = new Map<string, any[]>();

    terminalCalls.forEach((terminalCall: any) => {
      const dateTimeKey = getTerminalCallDateTimeKey(terminalCall);

      if (!dateTimeKey) {
        return;
      }

      if (!terminalCallsByDateTime.has(dateTimeKey)) {
        terminalCallsByDateTime.set(dateTimeKey, []);
      }

      terminalCallsByDateTime.get(dateTimeKey)?.push(terminalCall);
    });

    const terminalCallSameDateTimeWarnings: any = {};

    terminalCallsByDateTime.forEach((calls) => {
      if (calls.length < 2) {
        return;
      }

      calls.forEach((terminalCall: any) => {
        getComparableIds(terminalCall).forEach((id) => {
          terminalCallSameDateTimeWarnings[id] = {
            count: calls.length,
            dateLabel: getTerminalCallSameDateTimeLabel(terminalCall, t),
          };
        });
      });
    });

    const selectedTerminalCallFilterId = normalizeApiId(
      selectedTerminalCallFilterValue
    );

    const selectedTerminalCall =
      selectedTerminalCallFilterValue && selectedTerminalCallFilterId
        ? terminalCalls.find(
            (terminalCall: any) =>
              normalizeApiId(terminalCall) === selectedTerminalCallFilterId
          ) || selectedTerminalCallFilterValue
        : selectedTerminalCallFilterValue || null;

    const selectedTerminalCallObject =
      selectedTerminalCall && normalizeApiId(selectedTerminalCall)
        ? terminalCallMap.get(normalizeApiId(selectedTerminalCall) as string) ||
          selectedTerminalCall
        : selectedTerminalCall;

    const getResolvedContainer = (containerRef: any) => {
      const matchedContainer = getComparableIds(containerRef)
        .map((id) => containerMap.get(id))
        .find(Boolean);

      return matchedContainer || containerRef;
    };

    const enrichContainer = (
      container: any,
      fallbackLoadingTerminalCall: any = null,
      fallbackUnloadingTerminalCall: any = null
    ) => {
      const loadingTerminalCall =
        getComparableIds(container?.loadingTerminalCall)
          .map((id) => terminalCallMap.get(id))
          .find(Boolean) ||
        container?.loadingTerminalCall ||
        fallbackLoadingTerminalCall ||
        null;

      const unloadingTerminalCall =
        getComparableIds(container?.unloadingTerminalCall)
          .map((id) => terminalCallMap.get(id))
          .find(Boolean) ||
        container?.unloadingTerminalCall ||
        fallbackUnloadingTerminalCall ||
        null;

      return {
        ...container,
        loadingTerminalCall,
        unloadingTerminalCall,
      };
    };

    const allEnrichedContainers = rawContainers
      .map(getResolvedContainer)
      .filter(Boolean)
      .map((container: any) => enrichContainer(container));

    const loadingContainersForSelectedTerminalCall = selectedTerminalCallObject
      ? normalizeCollection(selectedTerminalCallObject.loadingContainers)
          .map(getResolvedContainer)
          .filter(Boolean)
          .map((container: any) =>
            enrichContainer(container, selectedTerminalCallObject, null)
          )
      : [];

    const unloadingContainersForSelectedTerminalCall = selectedTerminalCallObject
      ? normalizeCollection(selectedTerminalCallObject.unloadingContainers)
          .map(getResolvedContainer)
          .filter(Boolean)
          .map((container: any) =>
            enrichContainer(container, null, selectedTerminalCallObject)
          )
      : [];

    return {
      terminalCalls,
      groupedTerminalCallsByDate,
      terminalCallCountsById,
      terminalCallSameDateTimeWarnings,
      loadingTrajectories: buildTrajectoriesFromContainers(
        sortContainersByUid(loadingContainersForSelectedTerminalCall),
        "loading"
      ),
      unloadingTrajectories: buildTrajectoriesFromContainers(
        sortContainersByUid(unloadingContainersForSelectedTerminalCall),
        "unloading"
      ),
      allEnrichedContainers,
      selectedTerminalCall: selectedTerminalCallObject,
      loading: Boolean(
        selectedTerminalCallsState?.loading || selectedContainersState?.loading
      ),
    };
  }, [
    selectedTerminalCallsState,
    selectedContainersState,
    selectedTerminalCallFilterValue,
    t,
  ]);

  const selectedTerminalCallId = normalizeApiId(selectedTerminalCall);
  const voyageId = getEntityId(voyage);

  const duplicateGroups = useMemo(() => {
    const byDateTime = new Map<string, any[]>();
    terminalCalls.forEach((tc: any) => {
      const key = getTerminalCallDateTimeKey(tc);
      if (!key) return;
      const list = byDateTime.get(key) || [];
      list.push(tc);
      byDateTime.set(key, list);
    });
    const groups: Array<{ dateLabel: string; calls: any[] }> = [];
    byDateTime.forEach((calls) => {
      if (calls.length < 2) return;
      const dateLabel = getTerminalCallSameDateTimeLabel(calls[0], t);
      groups.push({ dateLabel, calls });
    });
    return groups;
  }, [terminalCalls, t]);

  const hasDuplicateTimings = duplicateGroups.length > 0;

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedContainerIds, setSelectedContainerIds] = useState<string[]>([]);
  const [bulkLoadingCall, setBulkLoadingCall] = useState("");
  const [bulkUnloadingCall, setBulkUnloadingCall] = useState("");
  const [savingBulkUpdate, setSavingBulkUpdate] = useState(false);
  const [savingBulkDelete, setSavingBulkDelete] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");
  const [draggingTerminalCallId, setDraggingTerminalCallId] = useState("");
  const [dropZoneKey, setDropZoneKey] = useState("");
  const dropZoneLeaveTimeoutRef = useRef<number | null>(null);
  const [movingTerminalCall, setMovingTerminalCall] = useState(false);
  const [terminalCallMoveError, setTerminalCallMoveError] = useState("");
  const [terminalCallMoveSuccess, setTerminalCallMoveSuccess] = useState("");
  const [singleConfirmOpen, setSingleConfirmOpen] = useState(false);
  const [singleChangeRequest, setSingleChangeRequest] = useState<{
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  } | null>(null);
  const [savingSingleUpdate, setSavingSingleUpdate] = useState(false);
  const [sameTimeDialogOpen, setSameTimeDialogOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [timeOffsets, setTimeOffsets] = useState<Record<string, number>>({});

  useEffect(() => {
    if (sameTimeDialogOpen) {
      const offsets: Record<string, number> = {};
      duplicateGroups.forEach((group) => {
        group.calls.forEach((tc, ci) => {
          if (ci > 0) offsets[getEntityId(tc)] = ci * 10;
        });
      });
      setTimeOffsets(offsets);
    }
  }, [sameTimeDialogOpen, duplicateGroups]);

  const busy =
    loading ||
    savingBulkUpdate ||
    savingBulkDelete ||
    savingSingleUpdate ||
    movingTerminalCall;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim().toLowerCase());
    }, 250);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedContainerIds([]);
    setBulkLoadingCall("");
    setBulkUnloadingCall("");
    setBulkConfirmOpen(false);
    setBulkDeleteConfirmOpen(false);
    setBulkError("");
    setBulkSuccess("");
  }, [voyageId]);

  useEffect(() => {
    if (!terminalCalls.length) {
      return;
    }

    const firstTerminalCall = terminalCalls[0];
    const filterId = normalizeApiId(selectedTerminalCallFilterValue);

    if (!filterId) {
      dispatch(addSelectedTerminalCallFilterValue(firstTerminalCall));
      return;
    }

    const stillExists = terminalCalls.some(
      (terminalCall: any) => normalizeApiId(terminalCall) === filterId
    );

    if (!stillExists) {
      dispatch(addSelectedTerminalCallFilterValue(firstTerminalCall));
    }
  }, [dispatch, terminalCalls, selectedTerminalCallFilterValue]);

  const sortedTerminalCallOptions = useMemo(() => {
    return sortTerminalCallsByDate(terminalCalls);
  }, [terminalCalls]);

  const terminalCallById = useMemo(() => {
    const map = new Map<string, any>();

    terminalCalls.forEach((terminalCall: any) => {
      getComparableIds(terminalCall).forEach((id) => {
        map.set(id, terminalCall);
      });
    });

    return map;
  }, [terminalCalls]);

  const allSearchableContainers = useMemo(() => {
    const map = new Map<string, any>();

    allEnrichedContainers.forEach((container: any) => {
      const containerId = getEntityId(container) || container?.uid;

      if (!containerId || map.has(containerId)) {
        return;
      }

      map.set(containerId, {
        ...container,
        searchText: getContainerSearchText(container),
      });
    });

    return Array.from(map.values());
  }, [allEnrichedContainers]);

  const hasSearchTerm = debouncedSearchTerm.length > 0;

  const searchedContainers = useMemo(() => {
    if (!debouncedSearchTerm) {
      return [];
    }

    return allSearchableContainers.filter((container: any) =>
      containerMatchesSearchTerm(container, debouncedSearchTerm)
    );
  }, [allSearchableContainers, debouncedSearchTerm]);

  const searchTrajectories = useMemo(() => {
    if (!debouncedSearchTerm) {
      return [];
    }

    return buildTrajectoriesFromContainers(searchedContainers, "loading");
  }, [debouncedSearchTerm, searchedContainers]);

  const visibleContainers = useMemo(() => {
    if (hasSearchTerm) {
      return searchedContainers;
    }

    const containerMap = new Map<string, any>();

    [
      ...loadingTrajectories.flatMap((trajectory: any) => trajectory.containers),
      ...unloadingTrajectories.flatMap((trajectory: any) => trajectory.containers),
    ].forEach((container: any) => {
      const containerId = getEntityId(container) || container?.uid;

      if (containerId && !containerMap.has(containerId)) {
        containerMap.set(containerId, container);
      }
    });

    return Array.from(containerMap.values());
  }, [hasSearchTerm, loadingTrajectories, searchedContainers, unloadingTrajectories]);

  const visibleContainerCount = visibleContainers.length;

  const allVisibleContainerIds = useMemo(() => {
    return Array.from(new Set(visibleContainers.map(getEntityId).filter(Boolean)));
  }, [visibleContainers]);

  const selectedVisibleContainers = useMemo(() => {
    return visibleContainers.filter((container: any) =>
      selectedContainerIds.includes(getEntityId(container))
    );
  }, [visibleContainers, selectedContainerIds]);

  const currentLoadingTerminalCallIds = useMemo(() => {
    return Array.from(
      new Set(
        selectedVisibleContainers
          .map((container: any) => getEntityId(container?.loadingTerminalCall))
          .filter(Boolean)
      )
    );
  }, [selectedVisibleContainers]);

  const currentUnloadingTerminalCallIds = useMemo(() => {
    return Array.from(
      new Set(
        selectedVisibleContainers
          .map((container: any) => getEntityId(container?.unloadingTerminalCall))
          .filter(Boolean)
      )
    );
  }, [selectedVisibleContainers]);

  const currentLoadingTerminalCallId =
    currentLoadingTerminalCallIds.length === 1
      ? currentLoadingTerminalCallIds[0]
      : "";

  const currentUnloadingTerminalCallId =
    currentUnloadingTerminalCallIds.length === 1
      ? currentUnloadingTerminalCallIds[0]
      : "";

  const currentLoadingTerminalCall = currentLoadingTerminalCallId
    ? terminalCallById.get(currentLoadingTerminalCallId)
    : null;

  const currentUnloadingTerminalCall = currentUnloadingTerminalCallId
    ? terminalCallById.get(currentUnloadingTerminalCallId)
    : null;

  const selectedBulkLoadingCall =
    bulkLoadingCall && bulkLoadingCall !== currentLoadingTerminalCallId
      ? bulkLoadingCall
      : "";

  const selectedBulkUnloadingCall =
    bulkUnloadingCall && bulkUnloadingCall !== currentUnloadingTerminalCallId
      ? bulkUnloadingCall
      : "";

  const selectedBulkLoadingTerminalCall = selectedBulkLoadingCall
    ? terminalCallById.get(selectedBulkLoadingCall)
    : null;

  const selectedBulkUnloadingTerminalCall = selectedBulkUnloadingCall
    ? terminalCallById.get(selectedBulkUnloadingCall)
    : null;

  const canSaveBulkUpdate =
    selectedContainerIds.length > 0 &&
    Boolean(selectedBulkLoadingCall || selectedBulkUnloadingCall) &&
    !busy;

  const canBulkDelete = selectedContainerIds.length > 0 && !busy;

  const getCurrentTerminalCallSummaryLabel = (type: "loading" | "unloading") => {
    const ids =
      type === "loading"
        ? currentLoadingTerminalCallIds
        : currentUnloadingTerminalCallIds;

    const currentTerminalCall =
      type === "loading"
        ? currentLoadingTerminalCall
        : currentUnloadingTerminalCall;

    if (currentTerminalCall) {
      return getTerminalCallOptionLabel(currentTerminalCall, t);
    }

    if (ids.length > 1) {
      return t("multipleCurrentTerminalCalls");
    }

    return t("noCurrentTerminalCall");
  };

  const bulkChangeSummary = useMemo(() => {
    const changes: any[] = [];

    if (selectedBulkLoadingCall && selectedBulkLoadingTerminalCall) {
      changes.push({
        key: "loading",
        title: t("loadingTerminalCallColumn"),
        currentValue: getCurrentTerminalCallSummaryLabel("loading"),
        nextValue: getTerminalCallOptionLabel(selectedBulkLoadingTerminalCall, t),
      });
    }

    if (selectedBulkUnloadingCall && selectedBulkUnloadingTerminalCall) {
      changes.push({
        key: "unloading",
        title: t("unloadingTerminalCallColumn"),
        currentValue: getCurrentTerminalCallSummaryLabel("unloading"),
        nextValue: getTerminalCallOptionLabel(selectedBulkUnloadingTerminalCall, t),
      });
    }

    return changes;
  }, [
    currentLoadingTerminalCall,
    currentLoadingTerminalCallIds,
    currentUnloadingTerminalCall,
    currentUnloadingTerminalCallIds,
    selectedBulkLoadingCall,
    selectedBulkLoadingTerminalCall,
    selectedBulkUnloadingCall,
    selectedBulkUnloadingTerminalCall,
    t,
  ]);

  useEffect(() => {
    setSelectedContainerIds((current) =>
      current.filter((id) => allVisibleContainerIds.includes(id))
    );
  }, [allVisibleContainerIds]);

  useEffect(() => {
    if (bulkLoadingCall && bulkLoadingCall === currentLoadingTerminalCallId) {
      setBulkLoadingCall("");
    }

    if (bulkUnloadingCall && bulkUnloadingCall === currentUnloadingTerminalCallId) {
      setBulkUnloadingCall("");
    }
  }, [
    bulkLoadingCall,
    bulkUnloadingCall,
    currentLoadingTerminalCallId,
    currentUnloadingTerminalCallId,
  ]);

  useEffect(() => {
    return () => {
      if (dropZoneLeaveTimeoutRef.current) {
        window.clearTimeout(dropZoneLeaveTimeoutRef.current);
      }
    };
  }, []);

  const selectedAllVisible =
    allVisibleContainerIds.length > 0 &&
    allVisibleContainerIds.every((id) => selectedContainerIds.includes(id));

  const selectedSomeVisible =
    allVisibleContainerIds.some((id) => selectedContainerIds.includes(id)) &&
    !selectedAllVisible;

  const toggleAllVisible = () => {
    if (busy) {
      return;
    }

    if (selectedAllVisible) {
      setSelectedContainerIds((current) =>
        current.filter((id) => !allVisibleContainerIds.includes(id))
      );
      return;
    }

    setSelectedContainerIds((current) =>
      Array.from(new Set([...current, ...allVisibleContainerIds]))
    );
  };

  const toggleGroupSelection = (containers: any[]) => {
    if (busy) {
      return;
    }

    const groupContainerIds = containers.map(getEntityId).filter(Boolean);

    if (groupContainerIds.length === 0) {
      return;
    }

    const allGroupSelected = groupContainerIds.every((id: string) =>
      selectedContainerIds.includes(id)
    );

    if (allGroupSelected) {
      setSelectedContainerIds((current) =>
        current.filter((id) => !groupContainerIds.includes(id))
      );
      return;
    }

    setSelectedContainerIds((current) =>
      Array.from(new Set([...current, ...groupContainerIds]))
    );
  };

  const toggleContainer = (container: any) => {
    if (busy) {
      return;
    }

    const containerId = getEntityId(container);

    if (!containerId) {
      return;
    }

    setSelectedContainerIds((current) => {
      if (current.includes(containerId)) {
        return current.filter((id) => id !== containerId);
      }

      return [...current, containerId];
    });
  };

  const clearSelection = () => {
    if (busy) {
      return;
    }

    setSelectedContainerIds([]);
    setBulkLoadingCall("");
    setBulkUnloadingCall("");
    setBulkConfirmOpen(false);
    setBulkDeleteConfirmOpen(false);
    setBulkError("");
    setBulkSuccess("");
  };

  const selectTerminalCall = useCallback(
    (terminalCall: any) => {
      if (draggingTerminalCallId || busy) {
        return;
      }

      setSearchTerm("");
      setDebouncedSearchTerm("");
      setSelectedContainerIds([]);
      setBulkLoadingCall("");
      setBulkUnloadingCall("");
      setBulkConfirmOpen(false);
      setBulkDeleteConfirmOpen(false);
      setBulkError("");
      setBulkSuccess("");
      dispatch(addSelectedTerminalCallFilterValue(terminalCall));
    },
    [busy, dispatch, draggingTerminalCallId]
  );

  const openTerminalCallCreateModal = useCallback(() => {
    if (busy) {
      return;
    }

    dispatch(
      showModal({
        title: t("addNewPlanning"),
        subtitle: t("addNewPlanningDescription"),
        description: "Creates new planning",
        form: "simpleterminalcall",
        formProps: {
          vessel: selectedTerminalCall?.vessel || voyage?.vessel,
          voyage,
          showTerminalField: true,
          callback: async () => {
            if (voyage) {
              await refreshVoyageData(voyage);
            }
          },
        },
        size: "compact",
      })
    );
  }, [busy, dispatch, refreshVoyageData, selectedTerminalCall, t, voyage]);

  const openBulkConfirmation = () => {
    if (!canSaveBulkUpdate) {
      return;
    }

    setBulkError("");
    setBulkSuccess("");
    setBulkConfirmOpen(true);
  };

  const openBulkDeleteConfirmation = () => {
    if (!canBulkDelete) {
      return;
    }

    setBulkError("");
    setBulkSuccess("");
    setBulkDeleteConfirmOpen(true);
  };

  const confirmBulkTerminalCallUpdate = async () => {
    if (!canSaveBulkUpdate) {
      return;
    }

    const payload: any = {
      containerIds: selectedContainerIds,
    };

    if (selectedBulkLoadingCall) {
      payload.loadingTerminalCall = selectedBulkLoadingCall;
    }

    if (selectedBulkUnloadingCall) {
      payload.unloadingTerminalCall = selectedBulkUnloadingCall;
    }

    try {
      setSavingBulkUpdate(true);
      setBulkError("");
      setBulkSuccess("");

      const response = await fetch("/api/containers/bulk-terminal-calls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);

        throw new Error(error?.message || response.statusText);
      }

      const result = await response.json().catch(() => null);

      const recalculatedMessage =
        result?.recalculatedTerminalCallCount !== undefined
          ? ` ${t("terminalCallsRecalculatedCount", {
              count: result.recalculatedTerminalCallCount,
            })}`
          : "";

      setBulkSuccess(
        result?.updatedCount
          ? `${t("containersUpdatedSuccessfullyCount", {
              count: result.updatedCount,
            })}${recalculatedMessage}`
          : `${t("containersUpdatedSuccessfully")}${recalculatedMessage}`
      );

      setBulkConfirmOpen(false);
      setSelectedContainerIds([]);
      setBulkLoadingCall("");
      setBulkUnloadingCall("");

      if (voyage) {
        await refreshVoyageData(voyage);
      }
    } catch (error: any) {
      setBulkError(error?.message || t("failedToUpdateSelectedContainers"));
    } finally {
      setSavingBulkUpdate(false);
    }
  };

  const confirmBulkDeleteContainers = async () => {
    if (!canBulkDelete) {
      return;
    }

    try {
      setSavingBulkDelete(true);
      setBulkError("");
      setBulkSuccess("");

      const response = await fetch("/api/containers/bulk-delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          containerIds: selectedContainerIds,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);

        throw new Error(error?.message || response.statusText);
      }

      const result = await response.json().catch(() => null);

      const recalculatedMessage =
        result?.recalculatedTerminalCallCount !== undefined
          ? ` ${t("terminalCallsRecalculatedCount", {
              count: result.recalculatedTerminalCallCount,
            })}`
          : "";

      setBulkSuccess(
        result?.deletedCount
          ? `${t("containersDeletedSuccessfullyCount", {
              count: result.deletedCount,
            })}${recalculatedMessage}`
          : `${t("containersDeletedSuccessfully")}${recalculatedMessage}`
      );

      setBulkDeleteConfirmOpen(false);
      setSelectedContainerIds([]);
      setBulkLoadingCall("");
      setBulkUnloadingCall("");

      if (voyage) {
        await refreshVoyageData(voyage);
      }
    } catch (error: any) {
      setBulkError(error?.message || t("failedToDeleteSelectedContainers"));
    } finally {
      setSavingBulkDelete(false);
    }
  };

  const openSingleTerminalCallConfirmation = ({
    container,
    type,
    terminalCall,
  }: {
    container: any;
    type: "loading" | "unloading";
    terminalCall: any;
  }) => {
    if (busy) {
      return;
    }

    setSingleChangeRequest({
      container,
      type,
      terminalCall,
    });
    setSingleConfirmOpen(true);
    setBulkError("");
    setBulkSuccess("");
  };

  const confirmSingleTerminalCallUpdate = async () => {
    if (!singleChangeRequest || savingSingleUpdate) {
      return;
    }

    const containerId = getEntityId(singleChangeRequest.container);
    const terminalCallId = getEntityId(singleChangeRequest.terminalCall);

    if (!containerId || !terminalCallId) {
      setBulkError(t("unableToUpdateContainerMissingIds"));
      return;
    }

    const payload: any = {
      containerIds: [containerId],
    };

    if (singleChangeRequest.type === "loading") {
      payload.loadingTerminalCall = terminalCallId;
    }

    if (singleChangeRequest.type === "unloading") {
      payload.unloadingTerminalCall = terminalCallId;
    }

    try {
      setSavingSingleUpdate(true);
      setBulkError("");
      setBulkSuccess("");

      const response = await fetch("/api/containers/bulk-terminal-calls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);

        throw new Error(error?.message || response.statusText);
      }

      const result = await response.json().catch(() => null);

      const recalculatedMessage =
        result?.recalculatedTerminalCallCount !== undefined
          ? ` ${t("terminalCallsRecalculatedCount", {
              count: result.recalculatedTerminalCallCount,
            })}`
          : "";

      setBulkSuccess(
        `${t("containerUpdatedSuccessfully", {
          container: getContainerDisplayLabel(singleChangeRequest.container, t),
        })}${recalculatedMessage}`
      );

      setSingleConfirmOpen(false);
      setSingleChangeRequest(null);

      if (voyage) {
        await refreshVoyageData(voyage);
      }
    } catch (error: any) {
      setBulkError(error?.message || t("failedToUpdateContainerTerminalCall"));
    } finally {
      setSavingSingleUpdate(false);
    }
  };

  const setActiveDropZone = (key: string) => {
    if (dropZoneLeaveTimeoutRef.current) {
      window.clearTimeout(dropZoneLeaveTimeoutRef.current);
      dropZoneLeaveTimeoutRef.current = null;
    }

    setDropZoneKey((current) => (current === key ? current : key));
  };

  const clearActiveDropZoneWithDelay = () => {
    if (dropZoneLeaveTimeoutRef.current) {
      window.clearTimeout(dropZoneLeaveTimeoutRef.current);
    }

    dropZoneLeaveTimeoutRef.current = window.setTimeout(() => {
      setDropZoneKey("");
      dropZoneLeaveTimeoutRef.current = null;
    }, 120);
  };

  const clearTerminalCallDragState = () => {
    if (dropZoneLeaveTimeoutRef.current) {
      window.clearTimeout(dropZoneLeaveTimeoutRef.current);
      dropZoneLeaveTimeoutRef.current = null;
    }

    setDraggingTerminalCallId("");
    setDropZoneKey("");
  };

  const recalculateTerminalCallsAfterTerminalCallMove = async (
    terminalCall: any
  ) => {
    const terminalCallId = normalizeNumericId(terminalCall);

    if (!terminalCallId) {
      throw new Error(t("unableToRecalculateTerminalCallsMissingId"));
    }

    const response = await fetch(
      `/api/terminal_call/${terminalCallId}/recalculate-voyage`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);

      throw new Error(error?.message || t("failedToRecalculateTerminalCalls"));
    }

    return response.json().catch(() => null);
  };

  const moveTerminalCallBetween = async ({
    movedTerminalCall,
    previousTerminalCall,
    nextTerminalCall,
  }: {
    movedTerminalCall: any;
    previousTerminalCall?: any;
    nextTerminalCall?: any;
  }) => {
    const movedTerminalCallUrl = getTerminalCallPatchUrl(movedTerminalCall);

    if (!movedTerminalCallUrl) {
      setTerminalCallMoveError(t("unableToMoveTerminalCallMissingId"));
      return;
    }

    const movedTerminalCallId = getEntityId(movedTerminalCall);
    const previousTerminalCallId = getEntityId(previousTerminalCall);
    const nextTerminalCallId = getEntityId(nextTerminalCall);

    if (
      movedTerminalCallId &&
      (movedTerminalCallId === previousTerminalCallId ||
        movedTerminalCallId === nextTerminalCallId)
    ) {
      clearTerminalCallDragState();
      return;
    }

    const planned = getRandomDateBetweenTerminalCalls(
      previousTerminalCall,
      nextTerminalCall
    );

    try {
      setMovingTerminalCall(true);
      setTerminalCallMoveError("");
      setTerminalCallMoveSuccess("");

      const response = await fetch(movedTerminalCallUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          planned,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);

        throw new Error(error?.message || response.statusText);
      }

      const recalculationResult =
        await recalculateTerminalCallsAfterTerminalCallMove(movedTerminalCall);

      const recalculatedMessage =
        recalculationResult?.processed !== undefined
          ? t("terminalCallsRecalculatedCount", {
              count: recalculationResult.processed,
            })
          : t("terminalCallsRecalculated");

      setTerminalCallMoveSuccess(
        t("terminalCallMovedTo", {
          terminalCall: getTerminalCallOptionLabel(movedTerminalCall, t),
          date: dayjs(planned).format("DD MMM YYYY HH:mm"),
          recalculatedMessage,
        })
      );

      if (voyage) {
        await refreshVoyageData(voyage);
      }
    } catch (error: any) {
      setTerminalCallMoveError(error?.message || t("failedToMoveTerminalCall"));
    } finally {
      setMovingTerminalCall(false);
      clearTerminalCallDragState();
    }
  };

  const handleTerminalCallDragStart = (
    terminalCall: any,
    event: React.DragEvent<HTMLElement>
  ) => {
    const terminalCallId = getEntityId(terminalCall);

    if (!terminalCallId || busy) {
      event.preventDefault();
      return;
    }

    if (dropZoneLeaveTimeoutRef.current) {
      window.clearTimeout(dropZoneLeaveTimeoutRef.current);
      dropZoneLeaveTimeoutRef.current = null;
    }

    setDraggingTerminalCallId(terminalCallId);
    setDropZoneKey("");
    setTerminalCallMoveError("");
    setTerminalCallMoveSuccess("");

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", terminalCallId);
  };

  const fixDuplicateTimings = useCallback(async () => {
    for (const group of duplicateGroups) {
      for (let i = 1; i < group.calls.length; i++) {
        const tc = group.calls[i];
        const currentDate = getTerminalCallDateValue(tc);
        if (!currentDate) continue;
        const offset = timeOffsets[getEntityId(tc)] ?? i * 10;
        const newDate = dayjs(currentDate).add(offset, "minute").toISOString();
        const field = tc.planned ? "planned" : tc.arrival ? "arrival" : "operationStart";
        const url = getTerminalCallPatchUrl(tc);
        if (!url) continue;
        try {
          await fetch(url, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/merge-patch+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({ [field]: newDate }),
          });
        } catch (err) {
          console.error("Failed to fix duplicate timing for", tc["@id"], err);
        }
      }
    }
    setShowConfirmDialog(false);
    setSameTimeDialogOpen(false);
    if (voyage) {
      await refreshVoyageData(voyage);
    }
  }, [duplicateGroups, timeOffsets, voyage, refreshVoyageData]);

  const handleTerminalCallDrop = async ({
    event,
    previousTerminalCall,
    nextTerminalCall,
  }: {
    event: React.DragEvent<HTMLElement>;
    previousTerminalCall?: any;
    nextTerminalCall?: any;
  }) => {
    event.preventDefault();
    event.stopPropagation();

    const draggedTerminalCallId =
      draggingTerminalCallId || event.dataTransfer.getData("text/plain");

    const draggedTerminalCall = draggedTerminalCallId
      ? terminalCallById.get(draggedTerminalCallId)
      : null;

    if (!draggedTerminalCall || busy) {
      setDropZoneKey("");
      return;
    }

    await moveTerminalCallBetween({
      movedTerminalCall: draggedTerminalCall,
      previousTerminalCall,
      nextTerminalCall,
    });
  };

  const voyageName =
    voyage?.name ||
    voyage?.reference ||
    voyage?.["@id"]?.split("/").filter(Boolean).pop() ||
    t("noVoyageSelected");

  return (
    <Layout showNotesButton notesSlideoutOverlay={false}>
      <Box
        sx={{
          height: "calc(100vh - 110px)",
          display: "grid",
          gridTemplateColumns: "320px minmax(0, 1fr)",
          gap: 1.5,
          p: 1.5,
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: WRAPPER_BORDER_RADIUS,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box
            sx={{
              p: 1.25,
              borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
              backgroundColor: "#ffffff",
            }}
          >
            <Stack spacing={1} alignItems="stretch">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <PlannerViewSwitch activeView="list" disabled={busy} t={t} />
              </Box>

              <Box sx={{ width: "100%", minWidth: 0 }}>
                <VoyagePicker includeContainers={true} allowRefresh={true} />
              </Box>
            </Stack>

            <Typography
              sx={{
                mt: 0.65,
                fontSize: "0.78rem",
                color: "text.secondary",
                fontWeight: 700,
              }}
            >
              {t("dragTerminalCallMovePlannedTime")}
            </Typography>
          </Box>

          {movingTerminalCall ? (
            <LinearProgress
              sx={{
                height: 3,
                borderRadius: 999,
              }}
            />
          ) : null}

          {terminalCallMoveError ? (
            <Alert
              severity="error"
              onClose={() => setTerminalCallMoveError("")}
              sx={{ m: 1, mb: 0 }}
            >
              {terminalCallMoveError}
            </Alert>
          ) : null}

          {terminalCallMoveSuccess ? (
            <Alert
              severity="success"
              onClose={() => setTerminalCallMoveSuccess("")}
              sx={{ m: 1, mb: 0 }}
            >
              {terminalCallMoveSuccess}
            </Alert>
          ) : null}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 1,
            }}
          >
            {hasDuplicateTimings && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
                sx={{ mb: 1.5, py: 0, px: 1.5, cursor: "pointer" }}
                onClick={() => setSameTimeDialogOpen(true)}
              >
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  {t("timeConflictDetected", { count: duplicateGroups.length })}
                </Typography>
              </Alert>
            )}
            {groupedTerminalCallsByDate.map((group: any) => {
              const groupHasDuplicates = group.calls.some(
                (tc: any) => terminalCallSameDateTimeWarnings[getEntityId(tc)]
              );
              return (
              <Box key={group.date} sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 0.25, mb: 1 }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 950,
                      color: "text.primary",
                    }}
                  >
                    {group.date}
                  </Typography>
                </Stack>

                <Box sx={{ position: "relative" }}>
                  <Stack spacing={0}>
                    {group.calls.map((terminalCall: any, index: number) => {
                      const terminalCallId = getEntityId(terminalCall);
                    const previousTerminalCall = group.calls[index - 1] || null;
                    const nextTerminalCall = terminalCall;
                    const beforeDropZoneKey = `${group.date}-${index}-before`;

                    return (
                      <React.Fragment key={terminalCallId}>
                        <TerminalCallDropZone
                          dragging={Boolean(draggingTerminalCallId)}
                          active={dropZoneKey === beforeDropZoneKey}
                          t={t}
                          onDragOver={(event) => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                            setActiveDropZone(beforeDropZoneKey);
                          }}
                          onDragLeave={clearActiveDropZoneWithDelay}
                          onDrop={async (event) => {
                            await handleTerminalCallDrop({
                              event,
                              previousTerminalCall,
                              nextTerminalCall,
                            });
                          }}
                        />

                        <Box sx={{ mb: 0.85 }}>
                          <TerminalCallListItem
                            terminalCall={terminalCall}
                            selected={terminalCallId === selectedTerminalCallId}
                            counts={
                              terminalCallCountsById[
                                normalizeApiId(terminalCall) || ""
                              ] || {
                                loading: 0,
                                unloading: 0,
                              }
                            }
                            sameDateTimeWarning={
                              terminalCallSameDateTimeWarnings[terminalCallId] ||
                              terminalCallSameDateTimeWarnings[
                                normalizeApiId(terminalCall) || ""
                              ] ||
                              null
                            }
                            dragging={draggingTerminalCallId === terminalCallId}
                            moving={busy}
                            t={t}
                            onClick={() => selectTerminalCall(terminalCall)}
                            onDragStart={(event) =>
                              handleTerminalCallDragStart(terminalCall, event)
                            }
                            onDragEnd={clearTerminalCallDragState}
                          />
                        </Box>
                      </React.Fragment>
                    );
                  })}

                  {group.calls.length > 0 ? (
                    <TerminalCallDropZone
                      dragging={Boolean(draggingTerminalCallId)}
                      active={dropZoneKey === `${group.date}-after-last`}
                      t={t}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        setActiveDropZone(`${group.date}-after-last`);
                      }}
                      onDragLeave={clearActiveDropZoneWithDelay}
                      onDrop={async (event) => {
                        await handleTerminalCallDrop({
                          event,
                          previousTerminalCall: group.calls[group.calls.length - 1],
                          nextTerminalCall: null,
                        });
                      }}
                    />
                  ) : null}
                </Stack>
                {groupHasDuplicates && (
                  <Box
                    onClick={() => setSameTimeDialogOpen(true)}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 1,
                      background: (theme) =>
                        `linear-gradient(135deg, ${alpha(
                          theme.palette.warning.main,
                          0.20
                        )} 0%, ${alpha(
                          theme.palette.warning.light,
                          0.12
                        )} 100%)`,
                      backdropFilter: "blur(1px)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      transition: "background 0.2s ease",
                      "&:hover": {
                        background: (theme) =>
                          `linear-gradient(135deg, ${alpha(
                            theme.palette.warning.main,
                            0.28
                          )} 0%, ${alpha(
                            theme.palette.warning.light,
                            0.18
                          )} 100%)`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.85)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                        maxWidth: "90%",
                      }}
                    >
                      <WarningAmberIcon
                        sx={{ fontSize: 22, color: "warning.dark", flexShrink: 0 }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            color: "warning.dark",
                            lineHeight: 1.2,
                          }}
                        >
                          {t("duplicateTimeWarning")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.62rem",
                            fontWeight: 600,
                            color: "text.secondary",
                            mt: 0.25,
                          }}
                        >
                          {t("clickToResolve")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                </Box>
              </Box>
            );
            })}

            {groupedTerminalCallsByDate.length === 0 ? (
              <Alert severity="info">
                {t("noTerminalCallsFoundForSelectedVoyage")}
              </Alert>
            ) : null}
          </Box>

          <Box
            component={Paper}
            elevation={0}
            sx={{
              p: 1,
              borderRadius: 0,
              borderTop: "1px solid rgba(15, 23, 42, 0.08)",
              backgroundColor: "#ffffff",
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={openTerminalCallCreateModal}
              disabled={busy}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 850,
              }}
            >
              {t("planning")}
            </Button>
          </Box>
        </Paper>

        <Dialog
          open={sameTimeDialogOpen}
          onClose={() => setSameTimeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#d97706", fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 800 }}>
              {t("duplicateTimings")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              {t("duplicateTimingsDescription")}
            </Typography>
            {duplicateGroups.map((group, gi) => (
              <Box key={gi} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75, fontSize: "0.78rem" }}>
                  {group.dateLabel}
                </Typography>
                <Stack spacing={0.6}>
                  {group.calls.map((tc, ci) => {
                    const terminalName = getTerminalName(tc, t);
                    const isFirst = ci === 0;
                    const terminalColor = getTerminalCallColor(tc);
                    return (
                      <Paper
                        key={getEntityId(tc)}
                        elevation={0}
                        sx={(theme) => ({
                          p: 0.65,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.65,
                          borderRadius: "10px",
                          backgroundColor: isFirst
                            ? alpha(theme.palette.common.black, 0.03)
                            : alpha(terminalColor, 0.12),
                          border: isFirst
                            ? "1px solid rgba(0,0,0,0.08)"
                            : `1px solid ${alpha(terminalColor, 0.35)}`,
                        })}
                      >
                        <Box
                          sx={(theme) => ({
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: isFirst
                              ? `2px solid ${alpha(theme.palette.common.black, 0.12)}`
                              : `2px solid ${alpha(terminalColor, 0.5)}`,
                            backgroundColor: isFirst
                              ? alpha(theme.palette.common.black, 0.04)
                              : alpha(terminalColor, 0.18),
                          })}
                        >
                          {isFirst ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                          ) : (
                            <WarningAmberIcon sx={{ fontSize: 14, color: "#b45309" }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 800,
                              lineHeight: 1.2,
                              color: isFirst ? "text.primary" : "#92400e",
                            }}
                          >
                            {getTerminalCallNumber(tc) || terminalName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.66rem",
                              fontWeight: 600,
                              color: isFirst ? "text.secondary" : "#b45309",
                              mt: 0.15,
                            }}
                          >
                            {isFirst
                              ? terminalName
                              : `${dayjs(getTerminalCallDateValue(tc))
                                  .add(timeOffsets[getEntityId(tc)] ?? ci * 10, "minute")
                                  .format("HH:mm")} — ${terminalName}`}
                          </Typography>
                        </Box>
                        {!isFirst && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={timeOffsets[getEntityId(tc)] ?? ci * 10}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 1) {
                                  setTimeOffsets((prev) => {
                                    const next = { ...prev, [getEntityId(tc)]: val };
                                    const nextCall = ci < group.calls.length - 1 ? group.calls[ci + 1] : null;
                                    if (nextCall) {
                                      const nextVal = next[getEntityId(nextCall)] ?? (ci + 1) * 10;
                                      if (nextVal <= val) next[getEntityId(nextCall)] = val + 1;
                                    }
                                    const prevCall = ci > 1 ? group.calls[ci - 1] : null;
                                    if (prevCall) {
                                      const prevVal = next[getEntityId(prevCall)] ?? (ci - 1) * 10;
                                      if (prevVal >= val) next[getEntityId(prevCall)] = val - 1;
                                    }
                                    return next;
                                  });
                                }
                              }}
                              inputProps={{
                                min: (() => {
                                  const prevCall = ci > 1 ? group.calls[ci - 1] : null;
                                  return prevCall ? (timeOffsets[getEntityId(prevCall)] ?? (ci - 1) * 10) + 1 : 1;
                                })(),
                                max: (() => {
                                  const nextCall = ci < group.calls.length - 1 ? group.calls[ci + 1] : null;
                                  return nextCall ? (timeOffsets[getEntityId(nextCall)] ?? (ci + 1) * 10) - 1 : 60;
                                })(),
                                style: {
                                  width: 30,
                                  padding: "2px 4px",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  textAlign: "center",
                                },
                              }}
                              sx={{
                                width: 46,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "7px",
                                  backgroundColor: "rgba(255,255,255,0.85)",
                                  "& fieldset": {
                                    borderColor: "rgba(217,119,6,0.3)",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "rgba(217,119,6,0.5)",
                                  },
                                },
                              }}
                            />
                            <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color: "#b45309" }}>
                              min
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
            <Button onClick={() => setSameTimeDialogOpen(false)} size="small">
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={<WarningAmberIcon />}
              onClick={() => setShowConfirmDialog(true)}
            >
              {t("add10MinToDuplicates")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ px: 2, py: 1.25, fontSize: "1rem", fontWeight: 800 }}>
              {t("confirm")}
            </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1 }}>
            <Typography variant="body2">
              {t("confirmAdd10Min")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
            <Button onClick={() => setShowConfirmDialog(false)} size="small">
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={fixDuplicateTimings}
            >
              {t("confirm")}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            overflow: "hidden",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.4,
              borderRadius: WRAPPER_BORDER_RADIUS,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              alignItems: "center",
              gap: 2,
              backgroundColor: "#ffffff",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
              <Typography
                noWrap
                sx={{
                  fontSize: "1.42rem",
                  fontWeight: 950,
                  color: "text.primary",
                }}
              >
                {hasSearchTerm
                  ? t("searchResultsVoyage", { voyage: voyageName })
                  : t("loadingContainersVoyage", { voyage: voyageName })}
              </Typography>

              <Chip
                label={t("containersCount", { count: visibleContainerCount })}
                size="small"
                sx={{
                  height: 24,
                  backgroundColor: "#dbeafe",
                  color: "#1d4ed8",
                  fontWeight: 950,
                  fontSize: "0.72rem",
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                value={searchTerm}
                disabled={busy}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setBulkError("");
                  setBulkSuccess("");
                }}
                placeholder={t("searchContainerIdBicSeal")}
                sx={{
                  width: 330,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => setSearchTerm("")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Stack>
          </Paper>

          {busy ? (
            <LinearProgress
              sx={{
                height: 3,
                borderRadius: 999,
              }}
            />
          ) : null}

          {bulkError ? (
            <Alert severity="error" onClose={() => setBulkError("")}>
              {bulkError}
            </Alert>
          ) : null}

          {bulkSuccess ? (
            <Alert severity="success" onClose={() => setBulkSuccess("")}>
              {bulkSuccess}
            </Alert>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              px: 1.4,
              py: 1.25,
              borderRadius: WRAPPER_BORDER_RADIUS,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              backgroundColor: "#ffffff",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              display: "grid",
              gridTemplateColumns:
                "auto minmax(340px, 1fr) minmax(340px, 1fr) auto auto",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Checkbox
                checked={selectedAllVisible}
                indeterminate={selectedSomeVisible}
                onChange={toggleAllVisible}
                size="small"
                disabled={busy}
              />

              <Typography
                sx={{
                  fontSize: "0.86rem",
                  fontWeight: 900,
                  color: selectedContainerIds.length ? "#1976d2" : "text.primary",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedContainerIds.length} {String(t("selected")).toLowerCase()}
              </Typography>

              <IconButton
                size="small"
                aria-label={t("clearSelection")}
                onClick={clearSelection}
                disabled={selectedContainerIds.length === 0 || busy}
                sx={{
                  width: 40,
                  height: 40,
                  color: selectedContainerIds.length
                    ? "text.secondary"
                    : "action.disabled",
                  "&:hover": {
                    color: "#dc2626",
                    backgroundColor: "rgba(220, 38, 38, 0.06)",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <BulkTerminalCallSelect
              type="loading"
              value={bulkLoadingCall}
              currentTerminalCall={currentLoadingTerminalCall}
              currentTerminalCallId={currentLoadingTerminalCallId}
              terminalCallOptions={sortedTerminalCallOptions}
              disabled={busy}
              onChange={(nextValue) => {
                setBulkLoadingCall(nextValue);
                setBulkError("");
                setBulkSuccess("");
              }}
              t={t}
            />

            <BulkTerminalCallSelect
              type="unloading"
              value={bulkUnloadingCall}
              currentTerminalCall={currentUnloadingTerminalCall}
              currentTerminalCallId={currentUnloadingTerminalCallId}
              terminalCallOptions={sortedTerminalCallOptions}
              disabled={busy}
              onChange={(nextValue) => {
                setBulkUnloadingCall(nextValue);
                setBulkError("");
                setBulkSuccess("");
              }}
              t={t}
            />

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={openBulkConfirmation}
              disabled={!canSaveBulkUpdate}
              sx={{
                justifySelf: "start",
                textTransform: "none",
                fontWeight: 850,
                borderRadius: "10px",
                minWidth: 132,
                whiteSpace: "nowrap",
              }}
            >
              {t("makeChange")}
            </Button>

            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={openBulkDeleteConfirmation}
              disabled={!canBulkDelete}
              sx={{
                justifySelf: "start",
                textTransform: "none",
                fontWeight: 850,
                borderRadius: "10px",
                minWidth: 132,
                whiteSpace: "nowrap",
              }}
            >
              {t("bulkDelete")}
            </Button>
          </Paper>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            <Stack spacing={1.5}>
              {hasSearchTerm ? (
                <SearchResultsSection
                  groups={searchTrajectories}
                  selectedContainerIds={selectedContainerIds}
                  onToggleContainer={toggleContainer}
                  onToggleGroup={toggleGroupSelection}
                  terminalCallOptions={sortedTerminalCallOptions}
                  onRequestTerminalCallChange={openSingleTerminalCallConfirmation}
                  searchTerm={debouncedSearchTerm}
                  disabled={busy}
                  t={t}
                />
              ) : (
                <>
                  <ContainerSection
                    mode="loading"
                    groups={loadingTrajectories}
                    selectedContainerIds={selectedContainerIds}
                    onToggleContainer={toggleContainer}
                    onToggleGroup={toggleGroupSelection}
                    terminalCallOptions={sortedTerminalCallOptions}
                    onRequestTerminalCallChange={openSingleTerminalCallConfirmation}
                    searchTerm=""
                    forceExpanded={false}
                    disabled={busy}
                    t={t}
                  />

                  <ContainerSection
                    mode="unloading"
                    groups={unloadingTrajectories}
                    selectedContainerIds={selectedContainerIds}
                    onToggleContainer={toggleContainer}
                    onToggleGroup={toggleGroupSelection}
                    terminalCallOptions={sortedTerminalCallOptions}
                    onRequestTerminalCallChange={openSingleTerminalCallConfirmation}
                    searchTerm=""
                    forceExpanded={false}
                    disabled={busy}
                    t={t}
                  />
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={bulkConfirmOpen}
        onClose={() => {
          if (!savingBulkUpdate) {
            setBulkConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          {t("confirmTerminalCallChanges")}
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("aboutToUpdateSelectedContainers", {
              count: selectedContainerIds.length,
              containerLabel:
                selectedContainerIds.length === 1
                  ? t("containerSingular")
                  : t("containerPlural"),
            })}
          </Alert>

          <Stack spacing={1.25}>
            {bulkChangeSummary.map((change) => (
              <ChangeSummaryRow
                key={change.key}
                title={change.title}
                currentValue={change.currentValue}
                nextValue={change.nextValue}
                t={t}
              />
            ))}
          </Stack>

          {bulkChangeSummary.length === 0 ? (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.9rem",
                fontWeight: 700,
              }}
            >
              {t("noChangesSelected")}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setBulkConfirmOpen(false)}
            disabled={savingBulkUpdate}
            sx={{
              textTransform: "none",
              fontWeight: 850,
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={confirmBulkTerminalCallUpdate}
            disabled={!canSaveBulkUpdate}
            sx={{
              textTransform: "none",
              fontWeight: 850,
              borderRadius: "10px",
            }}
          >
            {savingBulkUpdate ? t("saving") : t("confirmChange")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => {
          if (!savingBulkDelete) {
            setBulkDeleteConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          {t("confirmBulkDeleteContainers")}
        </DialogTitle>

        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("aboutToDeleteSelectedContainers", {
              count: selectedContainerIds.length,
              containerLabel:
                selectedContainerIds.length === 1
                  ? t("containerSingular")
                  : t("containerPlural"),
            })}
          </Alert>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.9rem",
              fontWeight: 700,
            }}
          >
            {t("bulkDeleteContainersWarning")}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setBulkDeleteConfirmOpen(false)}
            disabled={savingBulkDelete}
            sx={{
              textTransform: "none",
              fontWeight: 850,
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={confirmBulkDeleteContainers}
            disabled={!canBulkDelete}
            sx={{
              textTransform: "none",
              fontWeight: 850,
              borderRadius: "10px",
            }}
          >
            {savingBulkDelete ? t("deleting") : t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={singleConfirmOpen}
        onClose={() => {
          if (!savingSingleUpdate) {
            setSingleConfirmOpen(false);
            setSingleChangeRequest(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          {t("confirmTerminalCallChange")}
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("aboutToChangeContainerTerminalCall", {
              type:
                singleChangeRequest?.type === "loading"
                  ? String(t("loading")).toLowerCase()
                  : String(t("unloading")).toLowerCase(),
              container: singleChangeRequest?.container
                ? getContainerDisplayLabel(singleChangeRequest.container, t)
                : "",
            })}
          </Alert>

          {singleChangeRequest ? (
            <Stack spacing={1.25}>
              <ChangeSummaryRow
                title={
                  singleChangeRequest.type === "loading"
                    ? t("loadingTerminalCallColumn")
                    : t("unloadingTerminalCallColumn")
                }
                currentValue={getTerminalCallOptionLabel(
                  singleChangeRequest.type === "loading"
                    ? singleChangeRequest.container?.loadingTerminalCall
                    : singleChangeRequest.container?.unloadingTerminalCall,
                  t
                )}
                nextValue={getTerminalCallOptionLabel(
                  singleChangeRequest.terminalCall,
                  t
                )}
                t={t}
              />

              <DetailedTerminalCallChoice
                terminalCall={singleChangeRequest.terminalCall}
                selected
                searchTerm={debouncedSearchTerm}
                t={t}
              />
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setSingleConfirmOpen(false);
              setSingleChangeRequest(null);
            }}
            disabled={savingSingleUpdate}
            sx={{
              textTransform: "none",
              fontWeight: 850,
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={confirmSingleTerminalCallUpdate}
            disabled={savingSingleUpdate || !singleChangeRequest}
            sx={{
              textTransform: "none",
              fontWeight: 850,
              borderRadius: "10px",
            }}
          >
            {savingSingleUpdate ? t("saving") : t("confirmChange")}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal />
    </Layout>
  );
};

export default VoyageTerminalCallContainerManager;
